import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CameraHealthBar } from '../components/CameraHealthBar';
import { DialogHapus } from '../components/DialogHapus';
import { TombolIkon } from '../components/IconAction';
import { GarisPembatas } from '../components/GarisPembatas';
import { PlaybackTimeline, TINGGI_PITA } from '../components/PlaybackTimeline';
import { PlayerBar } from '../components/PlayerBar';
import { StripSprite } from '../components/StripSprite';
import { TombolIsiPratayang } from '../components/TombolIsiPratayang';
import { KendaliSkala } from '../components/KendaliSkala';
import { cocokKamera } from '../lib/cariKamera';
import { mintaLayarPenuh } from '../lib/layarPenuh';
import { useZoomGambar } from '../lib/zoomGambar';
import { usePanahTv, type ArahPanah } from '../lib/panahTv';
import { RETENSI_MAX_HARI } from '../lib/retensi';
import { isStaff, useAuth } from '../hooks/useAuth';
import { useLayarLebar } from '../hooks/useLayarLebar';
import { useBahasa } from '../i18n';
import {
  api,
  createBookmark,
  deleteBookmark,
  dropExport,
  formatBytes,
  formatDuration,
  formatWib,
  formatWibJam,
  formatWibTanggal,
  listBookmarks,
  renameBookmark,
  type Bookmark,
  type Camera,
  type ExportJob,
  type Timeline,
  type TimelineSegment,
} from '../lib/api';

/**
 * Halaman Playback (§G10).
 *
 * Yang membedakannya dari daftar berkas yang dulu ada di sini: **segmentasi 15
 * menit tidak pernah kelihatan** (§D8). Orang memilih kamera dan rentang jam;
 * yang muncul satu video dan satu timeline.
 *
 * Dua sistem koordinat hidup berdampingan, dan hampir semua kerumitan di file
 * ini berasal dari perbedaan keduanya:
 *
 * - **Jam dinding** — yang dilihat orang, dan yang memuat bolong rekaman.
 * - **Waktu di dalam satu segmen** — yang dimengerti `<video>`.
 *
 * `timeline.segments` adalah jembatannya: tiap segmen membawa jam mulainya,
 * jadi posisi playhead selalu bisa dibolak-balik antara keduanya.
 */

/** Rentang default §G10 poin 1 — yang dicari orang hampir selalu baru terjadi. */
const RENTANG_DEFAULT_MS = 60 * 60 * 1000;
/** §D8.3 poin 5 — sama dengan batas server, ditegakkan lebih awal supaya pesannya lebih cepat. */
const RENTANG_UNDUH_MAX_S = 6 * 60 * 60;
/**
 * Lead time pra-muat yang diincar, dalam detik **jam dinding**.
 *
 * Cukup untuk NFS + jaringan LAN. Yang dulu salah bukan angkanya melainkan
 * satuannya: ia dibandingkan langsung dengan sisa durasi **rekaman**, jadi waktu
 * nyata yang tersisa untuk menjemput berkas berikutnya ikut terbagi lajunya —
 * 8 detik di 1×, tapi 0,5 detik di 16× dan 0,25 detik di 32×. Di situlah
 * "makin tinggi speed makin ngelag" lahir: di laju tinggi pra-muatnya praktis
 * tidak pernah sempat jalan, dan tiap batas berkas jadi pemuatan dari nol.
 */
const PRELOAD_SISA_S = 8;

/**
 * Ambang pra-muat: sisa detik **rekaman** saat cadangan mulai dijemput.
 *
 * - **1× tidak disentuh.** Ia mengembalikan angka lama apa adanya. Menonton
 *   normal adalah jalur yang selama ini benar, dan perbaikan untuk laju tinggi
 *   tidak berhak menanggung risiko di sana.
 * - **Di atas 1×, pra-muat mulai paling lambat di tengah berkas** — usul
 *   lapangan 2 Sep 2026, *"file berikutnya sudah di-load ketika yang lagi
 *   diputar sudah >50%"*. Untuk segmen 15 menit (§D2) ini memberi 28 detik
 *   nyata di 16× dan 14 detik di 32×, naik dari 0,5 dan 0,25 detik.
 *
 * `PRELOAD_SISA_S * laju` tetap ada sebagai lantai kedua: segmen pendek —
 * potongan terakhir sebelum perekam berhenti — separuhnya bisa cuma beberapa
 * detik, dan di situ yang menentukan tetap lead time nyata.
 *
 * Ongkos mulai sepagi ini kecil: elemen cadangan tidak diputar, jadi yang
 * ditarik kepala berkas (sidx + fragmen awal), bukan seluruh ~55 MB-nya.
 */
function ambangPraMuat(durasiS: number, laju: number): number {
  if (laju <= 1) return PRELOAD_SISA_S;
  return Math.max(PRELOAD_SISA_S * laju, durasiS / 2);
}
/** Langkah satu frame pada rekaman 4fps (§D3). */
const FRAME_MS = 250;
/**
 * Kecepatan shuttle J/L, mengikuti kebiasaan NLE.
 *
 * Sampai 32× (diminta lapangan 1 Sep 2026). Menyisir rekaman sejam untuk
 * mencari satu kejadian pada 8× masih makan 7,5 menit; pada 32× ia jadi
 * kurang dari dua menit, dan itu memang cara orang memakai halaman ini —
 * melintas cepat sampai ada yang bergerak, baru turun ke 1×.
 */
const SHUTTLE = [1, 2, 4, 8, 16, 32];

/**
 * Seretan yang berhenti lebih lama dari ini dianggap selesai — lajunya
 * dikembalikan ke nol supaya prefetch berhenti menarik ke arah yang sudah
 * ditinggalkan.
 */
const SCRUB_DIAM_MS = 400;

/**
 * Sejauh berapa **detik seretan ke depan** yang ditebak prefetch.
 *
 * 1,5 detik: cukup jauh untuk menutup satu putaran jaringan ke server (timeline
 * + `ffprobe` segmen terbuka ±0,2 detik, plus latensi), cukup pendek untuk
 * tidak menarik berjam-jam data setiap kali ada yang menyentak playhead sekali.
 */
const PREDIKSI_MS = 1_500;

/**
 * Jarak minimal antara dua permintaan perluasan.
 *
 * **Bukan debounce**, dan bedanya menentukan. Bentuk debounce — tunggu sampai
 * diam 250 ms — tidak pernah berbunyi selama jari masih menyeret, yaitu persis
 * saat datanya paling dibutuhkan. Yang ini plafon laju: permintaan pertama
 * berangkat segera, berikutnya paling cepat sekian milidetik kemudian.
 * Jendelanya sendiri hanya tumbuh (`min`/`max` terhadap yang sudah termuat),
 * jadi dua-tiga permintaan sudah cukup menutup seretan sepanjang apa pun.
 */
const JEDA_PERLUAS_MS = 400;

/**
 * Sesering apa ujung terbaru timeline disegarkan selama segmennya masih ditulis.
 *
 * Sama dengan cadence perakitan lembar sementara di server
 * (`LENSA_SPRITE_LIVE_FLUSH_MS`, bawaan 60 detik): menyegarkan lebih rapat
 * tidak menemukan sel baru, cuma menambah satu `ffprobe` per permintaan di
 * jalur timeline.
 */
const SEGAR_UJUNG_MS = 60_000;
/** Berapa lama `waiting` dibiarkan sebelum permintaan range dipancing ulang. */
const PANCING_MS = 8_000;
/** `waiting` yang lebih pendek dari ini bukan buffering, cuma seek biasa. */
const TAMPIL_SENDAT_MS = 1_000;

/**
 * Laju tertinggi yang benar-benar dijalankan `<video>` sendiri.
 *
 * Chrome dan Firefox sama-sama memotong `playbackRate` di 16×, dan keduanya
 * memotongnya **diam-diam**: menyetel 32× tidak melempar apa pun, elemennya
 * cuma tetap berjalan 16× sementara tombolnya menulis "32×". Angka yang bohong
 * di tombol lebih buruk daripada tidak ada tombolnya.
 *
 * Karena itu di atas angka ini lajunya dijahit: elemennya tetap berjalan pada
 * plafonnya, dan sisanya ditambal dengan menggeser posisi berkala — cara yang
 * persis sama dengan mundur (`§G10.1`), yang juga tidak bisa dikerjakan
 * `playbackRate`.
 */
const LAJU_MEDIA_MAX = 16;

/**
 * Selang tambalan laju di atas 16×.
 *
 * 250 ms, bukan 100 ms seperti mundur. Bedanya: mundur menggeser elemen yang
 * **berhenti**, sedangkan di sini elemennya sedang berjalan — tiap geseran
 * memotong dekode yang sedang jalan. Pada 250 ms yang terlihat empat detik
 * rekaman mengalir mulus lalu melompat empat detik, dan itu memang rupa
 * fast-forward. Pada 100 ms yang terlihat cuma tersendat.
 */
const LANGKAH_LAJU_MS = 250;

/**
 * Rentang cepat (§G10 poin 1) — pintu masuk halaman ini.
 *
 * Sampai hari ini pintunya dua kotak `datetime-local`: orang harus mengetik
 * tanggal **dan** jam, dua kali, dengan benar, sebelum melihat apa pun — dan
 * yang salah ketik satu angka mendarat di layar kosong tanpa penjelasan. Enam
 * dari sepuluh keluhan lapangan soal halaman ini berhenti di kalimat itu.
 *
 * Kotaknya tidak dibuang; ia pindah ke belakang "More filters". Yang berubah
 * jalur utamanya.
 *
 * **30D adalah chip terakhir, dan itu bukan kebetulan**: ia sama dengan
 * `RETENSI_MAX_HARI` (§D6). Menawarkan 90 hari berarti menawarkan rentang yang
 * dijamin kosong di dua pertiganya.
 */
const RENTANG_CEPAT = [
  { id: '1H', jam: 1, label: '1H' },
  { id: '6H', jam: 6, label: '6H' },
  { id: '24H', jam: 24, label: '24H' },
  { id: '7D', jam: 24 * 7, label: '7D' },
  { id: '30D', jam: 24 * RETENSI_MAX_HARI, label: '30D' },
] as const;

type IdRentang = (typeof RENTANG_CEPAT)[number]['id'];

/** `YYYY-MM-DD` untuk `<input type="date">`, waktu lokal (WIB di box ini). */
function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Ujung kanan rentang untuk satu tanggal.
 *
 * Hari ini berakhir **sekarang**, bukan tengah malam nanti: rentang yang
 * ujungnya di masa depan membuat timeline menyisakan ruang kosong sepanjang
 * sisa hari, dan playhead mendarat di tempat yang belum ada rekamannya. Hari
 * yang sudah lewat berakhir di tengah malamnya sendiri.
 */
function ujungHari(tanggal: string): number {
  const [y, m, d] = tanggal.split('-').map(Number);
  if (!y || !m || !d) return Date.now();
  const akhir = new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
  return Math.min(akhir, Date.now());
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function msDariInput(v: string): number | null {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

export function VideosPage() {
  const { t } = useBahasa();
  const { cameraId } = useParams<{ cameraId?: string }>();
  const navigate = useNavigate();

  const [cameras, setCameras] = useState<Camera[] | null>(null);
  const [fromInput, setFromInput] = useState(() => toLocalInput(new Date(Date.now() - RENTANG_DEFAULT_MS)));
  const [toInput, setToInput] = useState(() => toLocalInput(new Date()));
  /** Tanggal yang sedang dilihat — pasangan `rentangCepat`, bukan pengganti rentang. */
  const [tanggal, setTanggal] = useState(() => toDateInput(new Date()));
  /**
   * Chip rentang yang sedang menyala. `null` = orang menyetel jamnya sendiri
   * lewat "More filters", dan tidak ada chip yang boleh berpura-pura mewakilinya.
   */
  const [rentangCepat, setRentangCepat] = useState<IdRentang | null>('1H');
  /** Kotak jam manual di balik "More filters" — jalur lama, tetap ada. */
  const [filterLain, setFilterLain] = useState(false);
  /**
   * Dinaikkan tiap kali rentang diubah lewat chip atau tanggal.
   *
   * Chip yang harus disusul klik "Show" bukan chip, cuma tombol yang mengisi
   * kotak — dan itu persis kerumitan yang sedang dibuang. Tapi `muat()` membaca
   * `fromMs`/`toMs` hasil render sebelumnya, jadi memanggilnya langsung di
   * `onClick` akan memuat rentang yang lama. Penghitung ini yang menunda
   * pemanggilannya sampai state-nya benar-benar terpasang.
   */
  const [pemicuMuat, setPemicuMuat] = useState(0);
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [memuat, setMemuat] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [posMs, setPosMs] = useState<number | null>(null);
  const [main, setMain] = useState(false);
  const [rate, setRate] = useState(1);
  const [mundur, setMundur] = useState(0);
  const [markIn, setMarkIn] = useState<number | null>(null);
  const [markOut, setMarkOut] = useState<number | null>(null);
  /**
   * Bookmark milik akun ini (§D8.7, §G10.7).
   *
   * Menggantikan "penanda" sesi yang dulu ada di sini — tidak bernama, tidak
   * tersimpan, hilang saat halaman dimuat ulang. Itu persis versi lebih buruk
   * dari yang diminta lapangan, dan mempertahankan keduanya berarti dua benda
   * yang terlihat sama di timeline dengan dua nasib berbeda.
   */
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  /** Kotak label yang sedang terbuka: `atMs` posisi playhead saat `M` ditekan. */
  const [tandaBaru, setTandaBaru] = useState<{ atMs: number } | null>(null);
  const [labelBaru, setLabelBaru] = useState('');
  const [bookmarkError, setBookmarkError] = useState<string | null>(null);
  const [view, setView] = useState<{ from: number; to: number } | null>(null);
  const [bantuan, setBantuan] = useState(false);

  const [unduhan, setUnduhan] = useState<ExportJob | null>(null);
  /** Nama berkas export pilihan pemakai (§D8.8), diisi di sebelah tombol unduh. */
  const [namaExport, setNamaExport] = useState('');
  const [unduhError, setUnduhError] = useState<string | null>(null);

  const wadahRef = useRef<HTMLDivElement>(null);
  const vidA = useRef<HTMLVideoElement>(null);
  const vidB = useRef<HTMLVideoElement>(null);
  const [aktifA, setAktifA] = useState(true);
  /** Elemen yang terlihat sedang menunggu data (`waiting`) — lihat `onWaiting`. */
  const [tersendat, setTersendat] = useState(false);
  /**
   * **Id** segmen yang sedang termuat di masing-masing elemen — bukan indeksnya.
   *
   * Indeks bergeser setiap kali jendela muat melebar (§D8.6): satu gulungan ke
   * kiri menyisipkan segmen di depan, dan seluruh peta ini jadi bohong tanpa ada
   * yang menyentuh elemen videonya. Akibatnya dua-duanya buruk — seek ke berkas
   * yang sebenarnya sudah termuat memuat ulang (kotak hitam), dan elemen yang
   * dikira memegang berkas A ternyata memegang berkas B.
   */
  const termuat = useRef<{ a: string | null; b: string | null }>({ a: null, b: null });
  const [segAktif, setSegAktif] = useState<number | null>(null);

  /**
   * Nomor urut pemasangan segmen.
   *
   * Setiap permintaan menaikkannya, dan penangan `loadedmetadata`/`seeked` yang
   * datang terlambat memeriksanya lalu diam. Tanpa ini, menyeret playhead
   * melintasi beberapa berkas meninggalkan antrian penangan lama yang
   * masing-masing masih memegang offset lamanya — yang mendarat terakhir yang
   * menang, dan itu belum tentu yang diminta orang.
   */
  const tiketPasang = useRef(0);
  /** Elemen cadangan sedang disiapkan untuk menggantikan yang terlihat. */
  const menungguTukar = useRef(false);
  /** Playhead sedang dipegang orang — di timeline halaman atau di bilah. */
  const menyeret = useRef(false);

  /* ── Alat ukur jeda batas berkas (`?diag=1`) ───────────────────────────── */

  /**
   * "Ngadat saat ganti berkas" adalah keluhan yang tidak bisa dibuktikan selesai
   * dengan mata: sisa apa pun sesudah perbaikan selalu bisa terbaca sebagai
   * "masih terasa sedikit". Yang diukur di sini angka yang persis — jarak antara
   * **berkas berikutnya mulai diminta** dan **frame pertamanya benar-benar
   * tampil** (`tukar`), yaitu berapa lama gambar lama tertahan di layar.
   *
   * Ikut dicatat apakah pra-muatnya sempat jalan, dan itu bagian terpentingnya:
   * ia yang memisahkan "penjadwalannya salah" dari "penyimpanannya lambat".
   * Jeda besar dengan pra-muat *tidak* berarti ambangnya masih keliru; jeda
   * besar dengan pra-muat *ya* berarti obatnya sudah tidak ada di halaman ini.
   *
   * Dikunci di balik `?diag=1` dan dibaca sekali saat mount: ini alat ukur untuk
   * yang sedang memperbaiki, bukan untuk pos satpam.
   */
  const [diagAktif] = useState(
    () => typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('diag'),
  );
  const [diagBaris, setDiagBaris] = useState<{ jedaMs: number; praMuat: boolean; laju: number }[]>(
    [],
  );
  /**
   * Pendengar yang terpasang dan belum dilepas.
   *
   * Angka ini yang membuktikan tidak ada kebocoran: sesudah pemutaran tenang ia
   * harus kembali ke 0, dan sesi 10 menit di 32× tidak boleh membuatnya menanjak
   * terus. Dihitung juga saat `?diag=1` mati — biayanya satu penambahan integer,
   * dan penghitung yang cuma hidup di mode diagnosis tidak pernah menangkap
   * kebocoran yang muncul di pemakaian sungguhan.
   */
  const pendengarHidup = useRef(0);

  const fromMs = msDariInput(fromInput);
  const toMs = msDariInput(toInput);

  /**
   * Jendela yang benar-benar sudah dimuat, terpisah dari rentang yang diketik
   * di atas halaman (§D8.6).
   *
   * Keduanya dulu satu hal yang sama, dan itu yang mengurung timeline di dalam
   * jam yang kebetulan ditebak orang. Sekarang rentang di atas cuma titik awal;
   * menggulung keluar darinya melebarkan yang ini, dan isian di atas menyusul
   * mengikutinya.
   */
  const [jendelaMuat, setJendelaMuat] = useState<{ from: number; to: number } | null>(null);

  useEffect(() => {
    void api<{ cameras: Camera[] }>('/cameras')
      .then((r) => setCameras(r.cameras))
      .catch((e) => setError((e as Error).message));
  }, []);

  const kamera = useMemo(() => cameras?.find((c) => c.id === cameraId) ?? null, [cameras, cameraId]);

  /**
   * Batas **keras** sumbu waktu: lantai retensi kamera ini sampai sekarang.
   *
   * Dipindah ke atas 8 September 2026 karena `seek` sekarang memakainya —
   * sejak playhead tidak lagi dijepit ke jendela muat, inilah satu-satunya
   * jepitan yang tersisa, dan ia harus lahir sebelum jalur seek mana pun.
   */
  /**
   * "Sekarang" yang ikut maju. Dulu `Date.now()` dibaca sekali per kamera, jadi
   * halaman yang dibiarkan terbuka berjam-jam tidak bisa seek melewati jam ia
   * dibuka walau rekamannya sudah ada (audit 13 Sep 2026).
   */
  const [kini, setKini] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setKini(Date.now()), SEGAR_UJUNG_MS);
    return () => clearInterval(timer);
  }, []);
  const batasSumbu = useMemo(() => {
    const hari = kamera?.maxDay ?? 7;
    return { min: kini - hari * 86_400_000, max: kini };
  }, [kamera, kini]);

  const muat = useCallback(async () => {
    if (!cameraId || fromMs === null || toMs === null) {
      setTimeline(null);
      return;
    }
    if (fromMs >= toMs) {
      setError(t('The start time must be before the end time.'));
      return;
    }
    setMemuat(true);
    try {
      const q = new URLSearchParams({
        camera_id: cameraId,
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
      });
      const t = await api<Timeline>(`/recordings/timeline?${q}`);
      setTimeline(t);
      setJendelaMuat({ from: fromMs, to: toMs });
      setView({ from: fromMs, to: toMs });
      setMarkIn(null);
      setMarkOut(null);
      setSegAktif(null);
      termuat.current = { a: null, b: null };
      setPosMs(t.segments.length > 0 ? new Date(t.segments[0]!.startedAt).getTime() : null);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
      setTimeline(null);
    } finally {
      setMemuat(false);
    }
  }, [cameraId, fromMs, toMs]);

  /**
   * Terapkan satu chip rentang pada satu tanggal, lalu muat.
   *
   * Ditulis sebagai satu fungsi yang menerima keduanya — bukan dua handler yang
   * masing-masing membaca state pasangannya — karena mengganti tanggal dan
   * mengganti chip menghasilkan rentang baru lewat perhitungan yang sama persis.
   * Dua salinan perhitungan itu akan berpisah jalan pada kasus pertama yang
   * tidak terpikir, dan yang menemukannya orang yang mengira rentangnya salah.
   */
  const terapkanRentang = useCallback((id: IdRentang, tgl: string) => {
    const akhir = ujungHari(tgl);
    const jam = RENTANG_CEPAT.find((r) => r.id === id)?.jam ?? 1;
    setFromInput(toLocalInput(new Date(akhir - jam * 3600_000)));
    setToInput(toLocalInput(new Date(akhir)));
    setRentangCepat(id);
    setTanggal(tgl);
    setPemicuMuat((n) => n + 1);
  }, []);

  /**
   * Bookmark dimuat untuk **rentang filter** (§D8.7 no.4) — rentang setahun
   * tidak boleh menarik seluruh riwayat seseorang ke dalam satu respons.
   */
  const muatBookmark = useCallback(async () => {
    if (!cameraId || fromMs === null || toMs === null) {
      setBookmarks([]);
      return;
    }
    try {
      setBookmarks(
        await listBookmarks({
          cameraId,
          from: new Date(fromMs).toISOString(),
          to: new Date(toMs).toISOString(),
        }),
      );
    } catch {
      // Bookmark yang gagal dimuat tidak boleh menghentikan pemutaran — yang
      // jadi isi halaman ini rekamannya, dan penanda adalah lapisan di atasnya.
    }
  }, [cameraId, fromMs, toMs]);

  useEffect(() => {
    void muatBookmark();
  }, [muatBookmark]);

  /** Milidetik penanda, urut — dipakai lompatan `N` / `Shift`+`N`. */
  const waktuBookmark = useMemo(
    () => bookmarks.map((b) => new Date(b.at).getTime()).sort((a, b) => a - b),
    [bookmarks],
  );

  useEffect(() => {
    void muat();
    // Kamera **dan** pemicu rentang cepat. Sengaja bukan `fromMs`/`toMs`: kotak
    // jam manual di balik "More filters" tetap menunggu tombol Show, supaya
    // timeline tidak dimuat ulang di setiap ketikan angka jam. Yang memuat
    // seketika cuma chip dan tanggal — di situ satu klik memang satu maksud
    // yang sudah selesai.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, pemicuMuat]);

  /* ── Pemetaan jam dinding ↔ segmen ─────────────────────────────────────── */

  /**
   * Di-memo, bukan `timeline?.segments ?? []` polos.
   *
   * Array baru di setiap render membuat SELURUH turunannya berganti identitas —
   * `batas`, `cariSegmen`, `pasang`, `seek` — sehingga efek yang bergantung
   * padanya dibongkar-pasang di setiap render, termasuk interval mundur yang
   * berdetak tiap 100 ms.
   */
  const segments = useMemo(() => timeline?.segments ?? [], [timeline]);

  const batas = useCallback(
    (i: number) => {
      const s = segments[i]!;
      const a = new Date(s.startedAt).getTime();
      return { a, b: a + s.durationS * 1000 };
    },
    [segments],
  );

  /**
   * Segmen yang memuat jam ini. Kalau jamnya jatuh di bolong, yang dikembalikan
   * adalah segmen **berikutnya** — melompat maju ke rekaman terdekat lebih
   * berguna daripada menolak bergerak, dan itu juga yang dilakukan NLE saat
   * playhead mendarat di celah.
   */
  const cariSegmen = useCallback(
    (ms: number): number | null => {
      for (let i = 0; i < segments.length; i++) {
        const { a, b } = batas(i);
        if (ms < a) return i;
        if (ms <= b) return i;
      }
      return segments.length > 0 ? segments.length - 1 : null;
    },
    [segments, batas],
  );

  const elemen = useCallback(
    (which: 'a' | 'b') => (which === 'a' ? vidA.current : vidB.current),
    [],
  );
  const aktif = aktifA ? 'a' : 'b';
  const cadangan = aktifA ? 'b' : 'a';

  /**
   * Tampilkan segmen `i` pada posisi `offsetS`, opsional langsung diputar.
   *
   * Aturannya satu, dan seluruh fungsi ini ada untuk menegakkannya: **elemen
   * yang sedang terlihat tidak pernah diganti `src`-nya.** Mengganti `src`
   * mengosongkan gambarnya seketika, jadi yang dilihat orang kotak hitam selama
   * berkas berikutnya dijemput dari NFS — persis keluhan lapangan 30 Agu 2026,
   * *"kalau ganti footage di timeline, buffering hitam dulu; harusnya gambarnya
   * langsung keluar"*.
   *
   * Karena itu berkas lain selalu dimuat di elemen **cadangan** yang tidak
   * terlihat, dan peran keduanya baru ditukar sesudah frame pertamanya siap.
   * Gambar lama bertahan di layar sampai detik itu. Polanya sama persis dengan
   * yang sudah dipakai pergantian segmen otomatis (§D8.1) — yang selama ini
   * tidak memakainya cuma jalur seek.
   *
   * Seek **di dalam** satu berkas tetap lewat jalur cepat: tidak ada yang perlu
   * dimuat, jadi tidak ada yang perlu ditukar. Itu sebabnya pindah posisi di
   * berkas yang sama memang tidak pernah berkedip.
   */
  const tayangkan = useCallback(
    (i: number, offsetS: number, mainkan: boolean) => {
      const seg = segments[i];
      if (!seg) return;

      const elAktif = elemen(aktif);
      const tiket = ++tiketPasang.current;

      // Jalur cepat — yang terlihat sudah memegang berkas ini.
      if (elAktif && termuat.current[aktif] === seg.id && !elAktif.error) {
        menungguTukar.current = false;
        const terapkan = () => {
          if (tiket !== tiketPasang.current) return;
          elAktif.currentTime = Math.max(0, offsetS);
          elAktif.playbackRate = Math.min(rate, LAJU_MEDIA_MAX);
          if (mainkan) void elAktif.play().catch(() => undefined);
        };
        if (elAktif.readyState >= 1) terapkan();
        else elAktif.addEventListener('loadedmetadata', terapkan, { once: true });
        return;
      }

      // Kunjungan pertama: belum ada gambar apa pun yang perlu dijaga, jadi
      // berkas pertama boleh mendarat langsung di elemen yang terlihat.
      const kosong = termuat.current[aktif] === null;
      const sisi: 'a' | 'b' = kosong ? aktif : cadangan;
      const el = elemen(sisi);
      if (!el) return;

      // Dibaca SEBELUM `src` disentuh — sesudahnya jawabannya selalu "ya".
      const praMuatSempat = termuat.current[sisi] === seg.id;
      const mulaiMs = performance.now();

      // `el.error`: pra-muat cadangan (onTimeUpdate) yang gagal tetap tercatat
      // "termuat" padahal tidak ada apa-apa — tanpa muat ulang, `error`-nya
      // sudah lewat dan pemutaran beku selamanya di batas berkas.
      if (termuat.current[sisi] !== seg.id || el.error) {
        termuat.current[sisi] = seg.id;
        el.src = `/api/v1/recordings/${seg.id}/play`;
        el.load();
      }
      menungguTukar.current = !kosong;

      let selesai = false;
      let dibersihkan = false;

      function bersihkan(): void {
        // Idempoten: beberapa jalur bisa sampai ke sini untuk permintaan yang
        // sama (penjaga waktu yang tersusul, lalu `error` yang menyusul), dan
        // penghitung pendengar di bawah cuma benar kalau tiap permintaan
        // menguranginya tepat sekali.
        if (dibersihkan) return;
        dibersihkan = true;
        pendengarHidup.current -= 1;
        el!.removeEventListener('loadedmetadata', saatMetadata);
        el!.removeEventListener('seeked', saatSiap);
        el!.removeEventListener('loadeddata', saatSiap);
        el!.removeEventListener('error', saatGagal);
        clearTimeout(jaga);
      }

      /**
       * Tukar peran: yang tadi cadangan jadi yang terlihat.
       *
       * Dipanggil oleh siapa pun yang sampai lebih dulu di antara `seeked`,
       * `loadeddata`, dan penjaga waktu. Ketiganya perlu: `seeked` tidak
       * dijanjikan muncul kalau posisi yang diminta kebetulan sama dengan posisi
       * elemennya, dan berhenti selamanya di gambar lama jauh lebih buruk
       * daripada menukar seperempat detik terlalu cepat.
       */
      function tukar(): void {
        if (selesai) return;
        // Sudah disusul permintaan lain: yang ini tidak boleh menukar apa pun,
        // dan penangannya harus dilepas — kalau tidak, satu sesi menonton
        // panjang menumpuk pendengar mati di kedua elemen video.
        if (tiket !== tiketPasang.current) {
          selesai = true;
          bersihkan();
          return;
        }
        selesai = true;
        bersihkan();
        menungguTukar.current = false;
        // Di sinilah gambar benar-benar berganti, jadi di sinilah jedanya
        // berakhir — bukan di `seeked`, yang masih bisa datang sebelum
        // frame-nya terpasang.
        if (diagAktif) {
          const jedaMs = performance.now() - mulaiMs;
          setDiagBaris((r) => [{ jedaMs, praMuat: praMuatSempat, laju: rate }, ...r].slice(0, 10));
        }
        el!.playbackRate = Math.min(rate, LAJU_MEDIA_MAX);
        if (mainkan) void el!.play().catch(() => undefined);
        if (!kosong) {
          elAktif?.pause();
          setAktifA(sisi === 'a');
        }
      }

      function saatMetadata(): void {
        if (tiket !== tiketPasang.current) return;
        el!.currentTime = Math.max(0, offsetS);
      }

      /**
       * Siap = sudah punya frame di posisi yang diminta (`HAVE_CURRENT_DATA`).
       * Menukar sebelum itu berarti menukar ke kotak hitam — bug yang justru
       * sedang diperbaiki di sini.
       */
      function saatSiap(): void {
        if (el!.readyState >= 2) tukar();
      }

      /**
       * Berkasnya gagal dimuat — retensi menyapunya, atau NFS lepas sesaat.
       * Dilewati, bukan dibiarkan menggantung: pemutar yang berhenti di gambar
       * lama tanpa sebab persis sama membingungkannya dengan kotak hitam.
       */
      function saatGagal(): void {
        if (selesai) return;
        // Penjaga yang sama dengan `onError`: load yang kita batalkan sendiri
        // (`MEDIA_ERR_ABORTED`), atau `error` basi milik `src` lama yang tiba
        // sesudah `load()` mengosongkan `el.error`, bukan berkas rusak. Tanpa
        // ini satu seek melompati segmen berantai sampai ujung timeline — 19 %
        // sesi, audit 13 Sep 2026. Pendengar tetap terpasang untuk hasil aslinya.
        const kode = el!.error?.code;
        if (!kode || kode === 1) return;
        // Tersusul permintaan lain: bukan urusan kita lagi, tapi pendengar milik
        // permintaan ini tetap harus dilepas — lihat catatan di penjaga waktu.
        if (tiket !== tiketPasang.current) {
          selesai = true;
          bersihkan();
          return;
        }
        selesai = true;
        bersihkan();
        menungguTukar.current = false;
        termuat.current[sisi] = null;
        const berikut = i + 1;
        if (!segments[berikut]) {
          setMain(false);
          return;
        }
        setSegAktif(berikut);
        setPosMs(batasRef.current(berikut).a);
        tayangkanRef.current(berikut, 0, mainkan);
      }

      /**
       * Penjaga waktu — jaring pengaman kalau `seeked` tidak pernah datang
       * (browser boleh tidak mengirimnya kalau posisi yang diminta kebetulan
       * sama dengan posisi elemennya). Ia **tetap memeriksa kesiapan**: menukar
       * buta sesudah sekian detik cuma memindahkan kotak hitamnya ke belakang.
       */
      let sisaPeriksa = 30;
      /**
       * Rapat dulu (50 ms), longgar kemudian (250 ms).
       *
       * Yang dikejar bukan datanya — itu urusan `seeked` — melainkan jarak
       * antara "datanya sudah siap" dan "kita menyadarinya". Dengan satu irama
       * 250 ms, penukaran yang datanya siap sesaat setelah satu detak tetap
       * menunggu detak berikutnya; di 32× batas berkas datang tiap ~28 detik,
       * dan seperempat detik diam di situ terbaca sebagai tersendat. Sepuluh
       * periksa rapat pertama menutup hampir semua kasus nyata, sisanya kembali
       * longgar supaya sesi menonton panjang tidak membayar timer rapat terus.
       */
      const selangPeriksa = (): number => (sisaPeriksa > 20 ? 50 : 250);
      let jaga = setTimeout(function periksa(): void {
        if (selesai) return;
        /**
         * Sudah disusul permintaan lain — dan pendengar milik permintaan ini
         * WAJIB dilepas di sini.
         *
         * Dulu jalur ini cuma `return`. Setiap penukaran yang tersusul
         * meninggalkan satu set `seeked`/`loadeddata`/`error` mati menempel di
         * elemen videonya, dan di laju tinggi penukaran tersusul beberapa kali
         * per batas berkas. Satu sesi menonton panjang menumpuknya sampai
         * ratusan — masing-masing dipanggil di setiap seek berikutnya.
         */
        if (tiket !== tiketPasang.current) {
          selesai = true;
          bersihkan();
          return;
        }
        if (el!.readyState >= 2) {
          tukar();
          return;
        }
        /**
         * Habis jatah (~5,5 detik). Penangan `seeked`/`loadeddata`/`error`
         * sengaja TETAP terpasang, jadi kalau datanya menyusul belakangan
         * penukarannya tetap terjadi.
         *
         * Tapi `menungguTukar` dilepas di sini, dan itu wajib: sejak penjaga di
         * interval mundur dan tambalan laju berhenti menembak selama bendera ini
         * naik, bendera yang tidak pernah turun berarti pemutaran berhenti
         * selamanya. Melepasnya mengembalikan keadaan ke perilaku lama — posisi
         * kembali mengalir dari elemen yang terlihat, dan seek berikutnya
         * memulai permintaan baru. Itu jalur yang lebih buruk, tapi ia jalur
         * yang bergerak.
         */
        if (--sisaPeriksa <= 0) {
          menungguTukar.current = false;
          return;
        }
        jaga = setTimeout(periksa, selangPeriksa());
      }, 50);

      pendengarHidup.current += 1;
      if (el.readyState >= 1) saatMetadata();
      else el.addEventListener('loadedmetadata', saatMetadata, { once: true });
      el.addEventListener('seeked', saatSiap);
      // Hanya saat posisinya memang di awal berkas: di offset lain `loadeddata`
      // datang sebelum seek-nya selesai, dan menukar di situ berarti menampilkan
      // frame pertama berkas — bukan jam yang diminta.
      if (offsetS < 0.05) el.addEventListener('loadeddata', saatSiap);
      el.addEventListener('error', saatGagal);
    },
    [segments, elemen, aktif, cadangan, rate, diagAktif],
  );

  /**
   * Cermin `tayangkan` dan `batas` untuk dipakai dari dalam dirinya sendiri
   * (melewati segmen yang gagal dimuat). Fungsi tidak bisa memanggil versi
   * terbarunya sendiri lewat closure — yang tertangkap di sana selalu versi
   * render tempat ia dibuat.
   */
  const tayangkanRef = useRef(tayangkan);
  const batasRef = useRef(batas);
  useEffect(() => {
    tayangkanRef.current = tayangkan;
    batasRef.current = batas;
  }, [tayangkan, batas]);

  /** Pindahkan playhead ke jam dinding tertentu. */
  /**
   * Ke mana `seek(ms)` akan **mendaratkan** playhead — tanpa memindahkannya.
   *
   * Dipisah dari `seek` karena satu penelepon perlu tahu jawabannya lebih dulu:
   * Home/End (§G10.11 poin 6) harus bisa membedakan "playhead sudah di tepi
   * jendela" dari "belum", dan tepi jendela sering jatuh **di dalam bolong
   * rekaman** — di situ `seek` mendarat di awal segmen berikutnya, bukan di
   * jam yang diminta. Membandingkan posisi dengan `view.from` mentah akan
   * membuat Home terjebak: tekanan kedua meminta tempat yang sama, mendarat di
   * tempat yang sama, dan halamannya terlihat seperti tidak menerima tombol.
   */
  /**
   * Laju dan arah seretan playhead — bahan prefetch (§G10.11).
   *
   * Dilaporkan lapangan: *"saat videohead digeser atau di-scrub dengan cepat,
   * sepertinya sistem belum melakukan prefetch/prediksi ke arah pergerakan
   * videohead. Akibatnya timeline atau thumbnail di area tujuan baru mulai
   * dimuat setelah videohead sudah sampai di sana."*
   *
   * Yang dicatat di sini **milidetik timeline per milidetik nyata** — bukan
   * piksel per detik. Piksel tidak berarti apa-apa di sumbu yang bisa di-zoom:
   * seretan sejauh satu sentimeter berarti tiga detik pada zoom 15 menit dan
   * tiga jam pada zoom seminggu, dan yang perlu ditebak adalah jam berapa yang
   * akan diminta, bukan ke mana jarinya bergerak.
   *
   * Rata-rata bergerak (0,6 lama : 0,4 baru): satu langkah yang tersendat —
   * dan pada seretan jari itu biasa — tidak boleh membalik arah tebakan.
   * Berhenti lebih lama dari `SCRUB_DIAM_MS` menganggap seretannya selesai dan
   * mengembalikan laju ke nol, supaya prefetch tidak terus menarik data ke
   * arah yang sudah ditinggalkan.
   */
  const lajuScrub = useRef({ ms: 0, pada: 0, laju: 0 });
  const catatLajuScrub = useCallback((ms: number) => {
    const kini = performance.now();
    const s = lajuScrub.current;
    const dt = kini - s.pada;
    if (s.pada !== 0 && dt > 0 && dt < SCRUB_DIAM_MS) {
      s.laju = s.laju * 0.6 + ((ms - s.ms) / dt) * 0.4;
    } else {
      s.laju = 0;
    }
    s.ms = ms;
    s.pada = kini;
  }, []);

  /**
   * Segmen yang **benar-benar memuat** jam ini, atau `null`.
   *
   * Beda tajam dengan `cariSegmen`, dan bedanya itu inti perbaikan 8 September
   * 2026 sore: `cariSegmen` selalu menjawab sesuatu — segmen berikutnya kalau
   * jamnya jatuh di bolong, segmen terakhir kalau jamnya melewati ujung. Untuk
   * memutuskan berkas mana yang dipasang itu benar. Untuk memutuskan **di mana
   * playhead berdiri**, itu justru sumber bug: jawaban "yang terdekat" ditulis
   * balik ke posisi, dan playhead pindah sendiri.
   */
  const segmenMemuat = useCallback(
    (ms: number): number | null => {
      for (let i = 0; i < segments.length; i++) {
        const { a, b } = batas(i);
        // Setengah terbuka `[a, b)`: ujung segmen adalah awal segmen berikutnya
        // (celah 0 s, hari ini ±50 % batas) atau bahkan di dalamnya (tumpang
        // tindih). Dengan `<=` jam itu dijawab segmen LAMA, dan efek pemasang di
        // bawah membatalkan pergantian yang baru dilakukan `onEnded` —
        // pemutaran terpaku di frame terakhir (FASUM_B1_10 16:45:03, 18 Sep 2026).
        if (ms >= a && ms < b) return i;
      }
      return null;
    },
    [segments, batas],
  );

  /**
   * Ke mana `seek(ms)` akan **mendaratkan** playhead — tanpa memindahkannya.
   *
   * Dipakai Home/End (§G10.1) untuk membedakan "sudah di tepi jendela" dari
   * "belum". Sejak playhead tidak lagi dipentalkan (lihat `seek`), jawabannya
   * cuma jamnya sendiri yang dijepit batas sumbu.
   */
  const daratSeek = useCallback(
    (ms: number): { ms: number } | null => {
      if (!timeline || segments.length === 0) return null;
      return { ms: Math.min(Math.max(ms, batasSumbu.min), batasSumbu.max) };
    },
    [timeline, segments, batasSumbu],
  );

  /**
   * **Posisi playhead milik orang yang menggesernya, titik.**
   *
   * Dilaporkan lapangan 8 September 2026: *"ketika videohead masuk ke area
   * timeline yang belum selesai dimuat, videohead kadang malah 'mental' atau
   * lompat kembali ke titik tertentu. (…) Loading state tidak boleh
   * mempengaruhi posisi atau kontinuitas videohead."*
   *
   * Penyebabnya persis di sini. Bentuk lamanya menjepit jam yang diminta ke
   * `jendelaMuat` — jendela yang **kebetulan sudah ditarik dari server** — lalu
   * menuliskan hasil jepitan itu balik ke `posMs`. Menyeret ke daerah yang
   * belum termuat karena itu berarti: jam dijepit ke tepi jendela, `cariSegmen`
   * menariknya lagi ke segmen terdekat, dan playhead mendarat di tempat yang
   * tidak pernah ditunjuk siapa pun. Semakin cepat diseret, semakin jauh
   * mentalnya — karena semakin sering ia keluar dari jendela.
   *
   * Sekarang dua hal dipisah tegas:
   *
   * 1. **`posMs` selalu jam yang diminta**, dijepit hanya oleh batas keras
   *    sumbu (lantai retensi … sekarang). Tidak ada jendela muat, tidak ada
   *    pembulatan ke segmen. Data yang belum sampai tidak pernah jadi alasan
   *    memindahkan playhead.
   * 2. **Elemen video menyusul kalau ia bisa.** Kalau ada segmen yang
   *    benar-benar memuat jam itu, berkasnya dipasang. Kalau tidak — bolong
   *    rekaman, atau daerahnya memang belum ditarik — gambar terakhir dibiarkan
   *    apa adanya dan efek di bawah memasangnya begitu datanya tiba.
   *
   * Yang ikut berubah: menyeret ke bolong tidak lagi melompat ke rekaman
   * berikutnya. Itu memang dulu disengaja, tapi ia tidak bisa dipisahkan dari
   * bug di atas — keduanya satu mekanisme. Lompatannya dipindah ke `putar()`,
   * tempat ia benar-benar dibutuhkan: menekan ▶ di tengah bolong maju ke
   * rekaman berikutnya, dan itu satu-satunya saat orang memang meminta
   * kelanjutan, bukan posisi.
   */
  const seek = useCallback(
    (ms: number) => {
      if (!timeline || segments.length === 0) return;
      const target = Math.min(Math.max(ms, batasSumbu.min), batasSumbu.max);
      setPosMs(target);
      catatLajuScrub(target);

      const i = segmenMemuat(target);
      if (i === null) return;
      setSegAktif(i);
      tayangkan(i, (target - batas(i).a) / 1000, main);
    },
    [timeline, segments, batasSumbu, segmenMemuat, batas, tayangkan, main, catatLajuScrub],
  );

  /**
   * Pasang berkasnya begitu daerah tempat playhead berdiri selesai dimuat.
   *
   * Pasangan dari aturan di atas: playhead boleh berdiri di tempat yang
   * datanya belum ada, jadi harus ada yang menyusulkan gambarnya sesudah data
   * itu tiba. Tanpa ini, menyeret jauh ke belakang akan meninggalkan gambar
   * lama di layar selamanya walau segmennya sudah ikut termuat.
   *
   * Tidak berjalan selama playhead dipegang: yang sedang menyeret akan
   * memanggil `seek` lagi beberapa milidetik kemudian, dan memasang berkas di
   * tengah seretan cuma menjemput berkas yang langsung ditinggalkan.
   */
  useEffect(() => {
    if (posMs === null || menyeret.current || segments.length === 0) return;
    const i = segmenMemuat(posMs);
    if (i === null || i === segAktif) return;
    setSegAktif(i);
    tayangkan(i, (posMs - batas(i).a) / 1000, main);
    // `main` sengaja tidak jadi dependensi: menekan play tidak boleh memasang
    // ulang berkas yang sudah terpasang.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posMs, segments, segAktif, segmenMemuat, batas, tayangkan]);

  /* ── Pemutaran ─────────────────────────────────────────────────────────── */

  // Segmen pertama dipasang begitu timeline siap, supaya frame pertama sudah
  // ada di layar sebelum ada yang menekan play — halaman yang membuka dengan
  // kotak hitam terbaca sebagai "tidak ada rekaman".
  useEffect(() => {
    if (!timeline || segments.length === 0 || segAktif !== null) return;
    setSegAktif(0);
    tayangkan(0, 0, false);
  }, [timeline, segments, segAktif, tayangkan]);

  const onTimeUpdate = useCallback(() => {
    const el = elemen(aktif);
    if (!el || segAktif === null || !segments[segAktif]) return;

    /**
     * Selama playhead dipegang orang, atau selama elemen cadangan sedang
     * disiapkan untuk menggantikan yang terlihat, posisi itu **milik mereka**.
     * Yang masih mengalir dari elemen yang terlihat cuma sisa posisi lama, dan
     * menuliskannya membuat gagang timeline melompat bolak-balik antara tempat
     * yang ditunjuk jari dan tempat video kebetulan sedang berada — itulah
     * "timeline kedip-kedip" yang dilaporkan 30 Agu 2026.
     */
    if (menyeret.current || menungguTukar.current) return;

    const { a } = batas(segAktif);
    setPosMs(a + el.currentTime * 1000);

    // §D8.1: cadangan mem-preload segmen berikutnya jauh sebelum yang sekarang
    // habis. Tanpa ini pergantian segmen berarti membuka koneksi baru ke NFS
    // saat frame terakhir sudah tayang — persis saat tidak ada waktu lagi.
    //
    // Ambangnya ikut laju (`ambangPraMuat`), bukan angka mati. Sisa yang
    // dihitung di sini detik REKAMAN, sedangkan yang dibutuhkan NFS detik jam
    // dinding, dan di 32× satu tidak lagi sama dengan yang lain.
    const durasi = el.duration || 0;
    const sisa = durasi - el.currentTime;
    const berikut = segAktif + 1;
    const segBerikut = segments[berikut];
    if (
      Number.isFinite(sisa) &&
      sisa > 0 &&
      sisa < ambangPraMuat(durasi, rate) &&
      segBerikut &&
      termuat.current[cadangan] !== segBerikut.id
    ) {
      const cad = elemen(cadangan);
      if (cad) {
        termuat.current[cadangan] = segBerikut.id;
        cad.src = `/api/v1/recordings/${segBerikut.id}/play`;
        cad.load();
      }
    }
  }, [elemen, aktif, cadangan, segAktif, segments, batas, rate]);

  /**
   * Segmen yang gagal dimuat dilewati, bukan menghentikan putaran.
   *
   * Server sudah menyaring segmen kerdil (§D8.1), tapi berkas bisa hilang di
   * tengah menonton — retensi menyapunya, atau volume NFS lepas sesaat. Kalau
   * `<video>` dibiarkan berhenti dengan `MEDIA_ELEMENT_ERROR`, yang terlihat
   * pemakai cuma kotak hitam tanpa sebab.
   */
  const onError = useCallback(() => {
    /**
     * Pemuatan yang **kita sendiri** batalkan juga tiba di sini, sebagai
     * `MEDIA_ERR_ABORTED`. Menghitungnya sebagai berkas rusak berarti setiap
     * seretan playhead yang melintasi berkas ikut memajukan segmen: pemutaran
     * lari sendiri ke ujung timeline lalu berhenti, dan sesudah itu tidak ada
     * lagi yang bisa diputar.
     */
    const kode = elemen(aktif)?.error?.code;
    if (!kode || kode === 1) return;

    const berikut = (segAktif ?? -1) + 1;
    if (!segments[berikut]) {
      setMain(false);
      return;
    }
    const { a } = batas(berikut);
    setSegAktif(berikut);
    setPosMs(a);
    tayangkan(berikut, 0, main);
  }, [elemen, aktif, segAktif, segments, batas, tayangkan, main]);

  /**
   * Buffering di tengah putar (`waiting`) dulu tidak ditangani sama sekali:
   * gambar diam tanpa tanda apa pun, dan kalau permintaan range-nya macet
   * (NFS tersendat, koneksi seluler putus sesaat) browser bisa menunggu
   * selamanya. Sekarang ada pemutar-putar, dan sesudah `PANCING_MS` tanpa
   * kemajuan posisinya di-seek ke tempat yang sama — itu memaksa browser
   * membuka permintaan baru tanpa mengosongkan gambar.
   */
  //
  // Ditunda `TAMPIL_SENDAT_MS`: tiap seek memancing `waiting` sesaat, dan di
  // 32× tambalan laju seek tiap 250 ms — tanpa jeda, pemutar-putarnya berkedip
  // terus padahal datanya datang 10–30 ms (Radian, 6 Okt 2026 18:22).
  const tundaSendat = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onWaiting = useCallback(() => {
    if (tundaSendat.current) return;
    tundaSendat.current = setTimeout(() => setTersendat(true), TAMPIL_SENDAT_MS);
  }, []);
  const onLancar = useCallback(() => {
    if (tundaSendat.current) clearTimeout(tundaSendat.current);
    tundaSendat.current = null;
    setTersendat(false);
  }, []);
  useEffect(() => onLancar, [onLancar]);
  useEffect(() => onLancar(), [aktifA, onLancar]);
  useEffect(() => {
    if (!tersendat || !main) return;
    const timer = setInterval(() => {
      const el = elemen(aktif);
      if (!el || menungguTukar.current || el.readyState >= 3 || el.error) return;
      el.currentTime = el.currentTime;
    }, PANCING_MS);
    return () => clearInterval(timer);
  }, [tersendat, main, elemen, aktif]);

  const onEnded = useCallback(() => {
    const berikut = (segAktif ?? -1) + 1;
    if (!segments[berikut]) {
      setMain(false);
      return;
    }

    /**
     * Segmen berikutnya dimulai dari **tumpang tindihnya**, bukan dari nol
     * (§D8.5).
     *
     * `-segment_atclocktime` memotong di keyframe berikutnya, jadi isi sebuah
     * segmen sedikit melewati jam mulai segmen sesudahnya — terukur ±2 detik.
     * Memulai yang berikutnya dari detik nol berarti dua detik itu diputar dua
     * kali, dan playhead di timeline melompat **mundur** sejauh itu di setiap
     * batas berkas. Yang terlihat pemakai: pemutaran "tersendat" tiap ganti
     * klip, padahal tidak ada satu frame pun yang hilang.
     *
     * Nilainya dihitung dari jam dinding kedua segmen, jadi ia otomatis nol
     * kalau memang tidak ada tumpang tindih, dan tidak pernah negatif kalau
     * justru ada bolong — bolong sungguhan memang harus terlihat sebagai bolong
     * (§D8.1), bukan ditutupi dengan memundurkan pemutaran.
     */
    const tumpang = Math.max(0, (batas(segAktif!).b - batas(berikut).a) / 1000);

    /**
     * Lewat `tayangkan`, bukan menukar peran buta.
     *
     * Elemen cadangan **belum tentu** memegang segmen ini: pra-muatnya bisa
     * tidak sempat jalan sama sekali — segmen yang lebih pendek dari ambang
     * pra-muat, laju 8×, atau NFS yang lambat menjawab. Penukaran buta di
     * keadaan itu memindahkan seluruh pemutaran ke elemen yang `src`-nya masih
     * kosong, dan karena penangan `ended`/`error` ikut pindah ke sana, tidak ada
     * lagi kejadian apa pun yang bisa membangunkannya. Yang dilihat pemakai:
     * kotak hitam yang tidak pernah pulih — *"footage berjalan dari timeline,
     * terus gak bisa playback"* (30 Agu 2026).
     *
     * Kalau pra-muatnya memang sempat jalan, jalurnya tetap secepat dulu:
     * berkasnya sudah di memori, jadi yang tersisa cuma satu seek.
     */
    setSegAktif(berikut);
    setPosMs(batas(berikut).a + tumpang * 1000);
    tayangkan(berikut, tumpang, true);
  }, [segAktif, segments, batas, tayangkan]);

  // Kecepatan diterapkan ke kedua elemen: yang cadangan hari ini jadi yang
  // aktif beberapa detik lagi, dan ia harus melanjutkan dengan kecepatan sama.
  useEffect(() => {
    const laju = Math.min(rate, LAJU_MEDIA_MAX);
    for (const el of [vidA.current, vidB.current]) if (el) el.playbackRate = laju;
  }, [rate, aktifA]);

  /**
   * ▶ — dan **satu-satunya tempat playhead boleh pindah sendiri**.
   *
   * `seek` sengaja tidak lagi membulatkan posisi ke segmen terdekat (lihat
   * catatannya): menyeret ke bolong rekaman berarti berdiri di bolong itu, dan
   * playhead yang melompat sendiri saat data belum siap adalah keluhan yang
   * memicu perubahan ini.
   *
   * Tapi lompatan itu tetap dibutuhkan di satu tempat: menekan ▶ sementara
   * playhead berdiri di bolong. Di situ yang diminta orang **kelanjutan**,
   * bukan posisi — dan pemutar yang diam tanpa penjelasan lebih buruk daripada
   * pemutar yang maju ke rekaman berikutnya. Pada rekaman berbolong "berikutnya"
   * bisa saja tiga jam kemudian, dan itu justru jawaban yang benar (§D8.1).
   */
  const putar = useCallback(() => {
    setMundur(0);
    const p = posRef.current;
    if (p !== null && segments.length > 0 && segmenMemuat(p) === null) {
      const i = cariSegmen(p);
      if (i !== null) {
        setSegAktif(i);
        setPosMs(batas(i).a);
        setMain(true);
        tayangkan(i, 0, true);
        return;
      }
    }
    setMain(true);
    void elemen(aktif)?.play().catch(() => undefined);
  }, [elemen, aktif, segments, segmenMemuat, cariSegmen, batas, tayangkan]);

  /**
   * Jeda — sekaligus **pulang ke 1×**.
   *
   * Laju cepat itu keadaan sementara: orang menekan ⏩ untuk menyusul sesuatu,
   * lalu menjeda begitu ketemu. Melanjutkan dari jeda itu dengan 8× yang masih
   * menempel berarti gambarnya langsung kabur lagi dari yang barusan dicari —
   * dilaporkan dari lapangan 31 Agu 2026. Yang menahan laju sekarang cuma
   * pemutaran yang benar-benar sedang berjalan.
   */
  const jeda = useCallback(() => {
    setMain(false);
    setMundur(0);
    setRate(1);
    elemen(aktif)?.pause();
  }, [elemen, aktif]);

  /**
   * Maju lebih cepat — tombol `L` dan tombol ⏩.
   *
   * Menekan berulang menaikkan kecepatan menyusuri `SHUTTLE`, persis kelakuan
   * shuttle di NLE: 1× → 2× → 4× → 8×, lalu mentok. Ia selalu memutar ke depan,
   * jadi menekannya saat sedang mundur adalah cara tercepat membalik arah.
   */
  const majuLebihCepat = useCallback(() => {
    setMundur(0);
    setRate((r) => SHUTTLE[Math.min(SHUTTLE.indexOf(r) + 1, SHUTTLE.length - 1)] ?? 1);
    putar();
  }, [putar]);

  /**
   * Mundur lebih cepat — tombol `J` dan tombol ⏪.
   *
   * Tidak memanggil `putar()`: mundur dikerjakan dengan menggeser posisi
   * berkala (lihat efek di bawah), bukan dengan memutar elemen video, jadi
   * elemennya justru harus tetap berhenti.
   */
  const mundurLebihCepat = useCallback(() => {
    setRate(1);
    setMain(false);
    setMundur((m) => (m === 0 ? 1 : SHUTTLE[Math.min(SHUTTLE.indexOf(m) + 1, SHUTTLE.length - 1)] ?? 8));
  }, []);

  /**
   * Kembali ke laju normal — dan tetap berjalan.
   *
   * Sampai 24 Agu 2026 satu-satunya jalan keluar dari 4× atau dari mundur
   * adalah `K`, yaitu **papan ketik**. Pemakai tetikus (dan seluruh pemakai
   * layar sentuh) terjebak: tiga tombol transport hanya bisa mempercepat, dan
   * ⏵ saat sedang 4× cuma menjeda. Dilaporkan sebagai *critical* dari lapangan:
   * *"untuk mbalikin jadi 1× harus K atau rewind sekali terus play. Repot."*
   *
   * Ia **memutar**, bukan menjeda — di situlah bedanya dengan `K`. Orang yang
   * menekan "1×" sedang menyusul sesuatu yang barusan lewat terlalu cepat; yang
   * dia mau adalah melihatnya pada laju biasa, bukan berhenti di satu bingkai.
   * `K` tetap ada untuk yang memang ingin berhenti (§G10.1).
   */
  const lajuNormal = useCallback(() => {
    setRate(1);
    putar();
  }, [putar]);

  /**
   * Lompat ke segmen sebelum / sesudah.
   *
   * Dipakai kelompok transport di bawah gambar, bilah pemutar di dalam gambar
   * (§G10.3), dan `PgUp`/`PgDn` (§G10.1). Sampai 8 September 2026 ia juga punya
   * sepasang panah besar yang melayang di kiri-kanan gambar; keduanya dibuang
   * atas permintaan lapangan — kendali yang menutupi gambar yang sedang
   * diperiksa, untuk perbuatan yang sudah punya tombolnya sendiri.
   *
   * Berpindah **berkas**, bukan bergeser sekian detik: yang dicari orang saat
   * menekannya "potongan berikutnya", dan pada rekaman yang berbolong potongan
   * berikutnya bisa saja tiga jam kemudian. Menggeser 15 menit di situ akan
   * mendarat di kekosongan dan terlihat seperti tombol yang rusak.
   *
   * Dihitung dari jam dinding playhead, bukan dari `segAktif`: saat playhead
   * baru diseret dan berkasnya belum sempat terpasang, `segAktif` masih
   * menunjuk berkas yang tadi.
   */
  const lompatSegmen = useCallback(
    (arah: -1 | 1) => {
      if (segments.length === 0) return;
      const acuan = posMs ?? new Date(segments[0]!.startedAt).getTime();
      // +1 detik supaya "berikutnya" tidak menemukan segmen yang sedang diputar
      // kembali karena playhead kebetulan persis di detik mulainya.
      const kandidat =
        arah === 1
          ? segments.find((sg) => new Date(sg.startedAt).getTime() > acuan + 1000)
          : [...segments].reverse().find((sg) => new Date(sg.startedAt).getTime() < acuan - 1000);
      if (kandidat) seek(new Date(kandidat.startedAt).getTime());
    },
    [segments, posMs, seek],
  );

  /**
   * Simpan frame yang sedang tampil sebagai PNG.
   *
   * Seluruhnya di klien lewat `<canvas>` — tidak ada endpoint baru, tidak ada
   * berkas yang menumpuk di server, dan yang tersimpan persis frame yang sedang
   * dilihat orangnya. Nama berkasnya memuat nama kamera dan jam dindingnya,
   * karena tangkapan layar yang bernama `snapshot(3).png` tidak bisa dipakai
   * sebagai lampiran laporan.
   *
   * `drawImage` pada elemen video yang belum punya frame melempar; `videoWidth`
   * nol adalah caranya menyatakan itu.
   */
  const simpanFrame = useCallback(() => {
    const el = (aktifA ? vidA : vidB).current;
    if (!el || el.videoWidth === 0) return;
    const kanvas = document.createElement('canvas');
    kanvas.width = el.videoWidth;
    kanvas.height = el.videoHeight;
    const ctx = kanvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(el, 0, 0);
    kanvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const jam = posMs === null ? '' : `_${formatWibTanggal(posMs)}_${formatWibJam(posMs)}`;
      a.download = `${(kamera?.name ?? 'lensa').replace(/[^\w-]+/g, '_')}${jam.replace(/[:/ ]/g, '-')}.png`;
      a.click();
      // Dilepas di putaran berikutnya: mencabutnya seketika membatalkan
      // unduhan yang belum sempat dimulai browser.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }, 'image/png');
  }, [aktifA, posMs, kamera]);

  /** Ukuran gambar yang benar-benar sedang didekode — untuk baris keterangan. */
  const [ukuranVideo, setUkuranVideo] = useState<{ w: number; h: number } | null>(null);

  /**
   * Batas keras sumbu timeline (§D8.6): dari lantai retensi kamera sampai
   * sekarang.
   *
   * Bukan angka bebas — di luar itu memang tidak ada apa pun untuk dilihat:
   * `max_day` adalah umur rekaman terlama yang disimpan kamera ini (§D6), dan
   * masa depan belum direkam. Membiarkan sumbu berjalan lebih jauh cuma
   * menghasilkan gulungan panjang berisi kekosongan.
   */

  /**
   * Lebarkan jendela muat mengikuti gulungan timeline (§D8.6).
   *
   * Sengaja **tidak** memakai `muat()`: fungsi itu memulai sesi baru — mark in/
   * out dibuang, playhead dikembalikan ke awal, segmen aktif direset. Melakukan
   * itu karena orang menggeser timeline dua sentimeter akan menghapus pekerjaan
   * yang sedang berjalan. Di sini yang berubah hanya daftar segmennya.
   *
   * `segAktif` adalah **indeks**, dan indeks bergeser begitu ada segmen baru
   * yang disisipkan di depan. Karena itu ia dipetakan ulang lewat id segmen yang
   * sedang diputar — kalau tidak, satu gulungan ke kiri akan memindahkan
   * pemutaran ke klip yang lain tanpa ada yang menyentuh playhead.
   */
  const memperluas = useRef(false);
  const perluas = useCallback(
    async (dari: number, sampai: number) => {
      if (!cameraId || memperluas.current) return;
      memperluas.current = true;
      try {
        const q = new URLSearchParams({
          camera_id: cameraId,
          from: new Date(dari).toISOString(),
          to: new Date(sampai).toISOString(),
        });
        const t = await api<Timeline>(`/recordings/timeline?${q}`);
        const idAktif = segAktif === null ? null : (segments[segAktif]?.id ?? null);

        setTimeline(t);
        setJendelaMuat({ from: dari, to: sampai });
        // Isian jam di atas TIDAK diisi dengan jendela muat ini. Jendela muat
        // sengaja melebar jauh melewati yang terlihat (margin dua kali lebar
        // layar) supaya gulungan berikutnya tidak menunggu jaringan — dan
        // menuliskannya ke isian membuat rentang yang diketik orang membengkak
        // sendiri setiap kali timeline digeser. Di ponsel, tempat orang
        // menggeser terus-menerus, rentangnya jadi berhari-hari dalam beberapa
        // usapan: *"timeline-nya mental jauh"* (31 Agu 2026). Yang mengisi isian
        // sekarang jendela yang **terlihat** — lihat efek di bawah.

        if (idAktif !== null) {
          const baru = t.segments.findIndex((x) => x.id === idAktif);
          setSegAktif(baru >= 0 ? baru : null);
          // `termuat` sengaja TIDAK ikut dikosongkan: ia dikunci pakai id
          // segmen, jadi berkas yang sudah ada di elemen tetap sah walau
          // indeksnya bergeser. Mengosongkannya berarti seek pertama sesudah
          // menggulung timeline selalu memuat ulang berkas yang sebenarnya
          // sudah ada — kotak hitam tanpa alasan.
        }
      } catch (e) {
        setError((e as Error).message);
      } finally {
        memperluas.current = false;
      }
    },
    [cameraId, segAktif, segments],
  );

  /**
   * Pemicunya: jendela yang terlihat menyentuh tepi yang sudah dimuat.
   *
   * Ambangnya seperlima lebar jendela, bukan tepat di tepi — memuat tepat saat
   * orang sudah sampai ujung berarti ia selalu melihat kekosongan sesaat lebih
   * dulu.
   */
  const perluasTerakhir = useRef(0);
  useEffect(() => {
    if (!view || !jendelaMuat) return;
    const rentang = view.to - view.from;
    const margin = rentang * 0.2;

    /**
     * Tebakan arah: sejauh mana playhead akan sampai kalau laju seretnya
     * bertahan `PREDIKSI_MS` lagi.
     *
     * Dijepit tiga kali lebar jendela — satu sentakan cepat pada zoom seminggu
     * bisa menghasilkan laju yang, kalau diteruskan, meminta berbulan-bulan
     * sekaligus. Yang dikejar prefetch adalah beberapa layar ke depan, bukan
     * seluruh arsip.
     */
    const laju = lajuScrub.current.laju;
    const tebakan = Math.min(rentang * 3, Math.abs(laju) * PREDIKSI_MS);
    const keKanan = laju > 0 ? tebakan : 0;
    const keKiri = laju < 0 ? tebakan : 0;

    /**
     * `posMs` ikut dihitung, bukan cuma `view`.
     *
     * Sejak playhead tidak lagi dipentalkan ke jendela muat (lihat `seek`), ia
     * bisa berdiri di luar daerah yang sudah ditarik — dan justru di situlah
     * datanya paling dibutuhkan. Menghitung dari `view` saja berarti daerah
     * tempat playhead berdiri baru ditarik setelah orangnya menggeser sumbunya
     * ke sana juga.
     */
    const butuhKiri = Math.min(view.from, posMs ?? view.from) - margin - keKiri;
    const butuhKanan = Math.max(view.to, posMs ?? view.to) + margin + keKanan;
    if (butuhKiri >= jendelaMuat.from && butuhKanan <= jendelaMuat.to) return;

    const dari = Math.max(batasSumbu.min, Math.min(jendelaMuat.from, butuhKiri - margin));
    const sampai = Math.min(batasSumbu.max, Math.max(jendelaMuat.to, butuhKanan + margin));
    if (dari >= jendelaMuat.from && sampai <= jendelaMuat.to) return;

    // Plafon laju, bukan debounce — lihat `JEDA_PERLUAS_MS`.
    const sisa = Math.max(0, JEDA_PERLUAS_MS - (Date.now() - perluasTerakhir.current));
    const t = setTimeout(() => {
      perluasTerakhir.current = Date.now();
      void perluas(dari, sampai);
    }, sisa);
    return () => clearTimeout(t);
  }, [view, jendelaMuat, posMs, batasSumbu, perluas]);

  /**
   * Ujung terbaru disegarkan sendiri selama segmennya masih ditulis (§D8.1).
   *
   * Tanpa ini, sprite sementara yang sudah rajin dirakit worker tiap menit
   * tidak akan pernah terlihat: timeline halaman ini dimuat sekali per
   * perubahan filter, jadi `durationS` segmen terbuka membeku pada angka saat
   * halaman dibuka dan strip tidak pernah menumbuhkan sel baru.
   *
   * Tiga pagar supaya ia tidak jadi polling yang membebani:
   *
   * 1. **Hanya kalau ada segmen yang benar-benar masih ditulis.** Arsip yang
   *    sudah lewat tidak berubah, dan menyegarkannya tidak menghasilkan apa pun.
   * 2. **Hanya kalau tabnya terlihat.** Halaman Playback sering ditinggal
   *    terbuka seharian di pos satpam; menyegarkan tab tersembunyi berarti satu
   *    `ffprobe` per menit di server untuk layar yang tidak ada yang lihat.
   * 3. **Tidak selama playhead dipegang.** Menukar daftar segmen di tengah
   *    seretan berarti indeks yang sedang dipegang berpindah di bawah tangan.
   *
   * `perluas` dipegang lewat ref, bukan lewat dependensi: fungsi itu lahir
   * ulang setiap kali `segAktif` berubah — yaitu tiap pergantian berkas saat
   * memutar — dan memasukkannya ke dependensi membuat interval 60 detiknya
   * dibongkar-pasang terus sampai tidak pernah sempat berbunyi.
   */
  const perluasRef = useRef(perluas);
  perluasRef.current = perluas;
  const adaTerbuka = segments.some((sg) => !sg.isComplete);

  useEffect(() => {
    if (!adaTerbuka || !jendelaMuat) return;
    const timer = setInterval(() => {
      if (document.hidden || menyeret.current) return;
      void perluasRef.current(jendelaMuat.from, Math.max(jendelaMuat.to, Date.now()));
    }, SEGAR_UJUNG_MS);
    return () => clearInterval(timer);
  }, [adaTerbuka, jendelaMuat]);

  /**
   * Cermin `posMs` yang selalu mutakhir, dibaca dari dalam interval mundur.
   *
   * Bentuk sebelumnya membaca posisi lewat updater `setPosMs` yang mengembalikan
   * nilai lama sambil menitipkan `seek` ke `queueMicrotask` — jalan memutar yang
   * menumpang efek samping di dalam updater, tempat React tidak menjanjikan
   * apa pun soal berapa kali ia dipanggil. Ref-nya melakukan hal yang sama
   * secara langsung dan bisa dibaca kapan saja.
   */
  const posRef = useRef<number | null>(null);
  useEffect(() => {
    posRef.current = posMs;
  }, [posMs]);

  /**
   * Mundur (tombol J).
   *
   * `<video>` tidak bisa diputar mundur — `playbackRate` negatif ditolak semua
   * browser. Yang dikerjakan di sini adalah menggeser posisi mundur secara
   * berkala, cara yang sama dipakai NLE berbasis web. Langkahnya 100 ms, bukan
   * per frame animasi: rekaman ini 4 fps, jadi menyeret lebih halus dari itu
   * hanya menghasilkan seek yang tidak mengubah gambar.
   */
  useEffect(() => {
    if (mundur === 0) return;
    elemen(aktif)?.pause();
    const timer = setInterval(() => {
      // Diam selama penukaran berkas berjalan — alasannya sama persis dengan
      // tambalan laju di bawah, dan mundur melintasi batas berkas kena yang
      // sama.
      if (menungguTukar.current) return;
      const p = posRef.current;
      if (p !== null) seek(p - mundur * 100);
    }, 100);
    return () => clearInterval(timer);
  }, [mundur, elemen, aktif, seek]);

  /**
   * Maju di atas plafon `playbackRate` — laju 32× (§G10.1, 1 September 2026).
   *
   * Elemennya tetap berjalan sendiri pada `LAJU_MEDIA_MAX`; yang ditambahkan di
   * sini cuma **selisihnya**. Pada 32×: video mengalir 16× dari dirinya sendiri,
   * dan tiap 250 ms playhead digeser maju 16× × 250 ms = 4 detik lagi. Dua-duanya
   * dijumlahkan di jam dinding, jadi yang terbaca di timeline benar-benar 32×.
   *
   * Titik tolaknya `posRef`, bukan hitungan sendiri: nilainya diperbarui
   * `onTimeUpdate` dari posisi elemen yang sungguhan. Dengan begitu tambalan ini
   * tidak pernah bisa lari dari videonya — kalau NFS tersendat dan elemennya
   * tertinggal, geseran berikutnya berangkat dari tempat elemennya benar-benar
   * berada, bukan dari tempat yang seharusnya.
   *
   * Di ≤16× efek ini tidak berbuat apa-apa: `playbackRate` sendirian sudah
   * cukup, dan menggeser posisi yang tidak perlu hanya memotong dekode.
   */
  useEffect(() => {
    if (!main || rate <= LAJU_MEDIA_MAX) return;
    const langkah = (rate - LAJU_MEDIA_MAX) * LANGKAH_LAJU_MS;
    const timer = setInterval(() => {
      /**
       * Diam selama penukaran berkas sedang berjalan.
       *
       * Selama itu `onTimeUpdate` menahan diri menulis posisi — posisi sedang
       * milik elemen cadangan — jadi `posRef` **beku**, dan tembakan berikutnya
       * berangkat dari titik yang sama persis. Yang terjadi tanpa penjaga ini:
       * tiap 250 ms `seek` menaikkan `tiketPasang`, yang membatalkan penukaran
       * yang sedang menunggu, memasang ulang pendengarnya, dan me-reset penjaga
       * waktunya dari nol — padahal penjaga itu satu-satunya yang bisa
       * menyelesaikan penukaran kalau `seeked` tidak muncul (dan ia tidak muncul
       * kalau posisi yang diminta sama dengan posisi elemennya, yang di sini
       * justru keadaan normalnya karena targetnya tidak berubah). Dua timer 250
       * ms saling mengejar, dan pemutaran berhenti beberapa detik di setiap
       * batas berkas: "makin tinggi speed makin ngelag".
       *
       * Berhenti menembak di sini tidak menahan pemutaran: elemen yang baru
       * tetap berjalan sendiri pada `LAJU_MEDIA_MAX`, dan tambalannya kembali
       * jalan begitu gambarnya tampil.
       */
      if (menungguTukar.current) return;
      const p = posRef.current;
      if (p !== null) seek(p + langkah);
    }, LANGKAH_LAJU_MS);
    return () => clearInterval(timer);
  }, [main, rate, seek]);

  /**
   * Layar penuh untuk pemutaran.
   *
   * Yang dijadikan layar penuh adalah **wadahnya**, bukan `<video>`-nya: kedua
   * elemen video bertukar peran di batas segmen (§D8.1), dan layar penuh yang
   * menempel pada salah satunya akan runtuh begitu pertukaran itu terjadi.
   */
  const layarPenuh = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.().catch(() => undefined);
      return;
    }
    if (wadahRef.current) mintaLayarPenuh(wadahRef.current);
  }, []);

  /**
   * Sedang layar penuh? Bukan sekadar hiasan ikon: bilah kendali di dalam
   * gambar membesar di layar penuh (jaraknya ke mata bertambah — di pos satpam
   * layarnya TV, bukan monitor sejengkal), dan tombol di pojok harus berhenti
   * menawarkan "masuk layar penuh" saat sudah di dalamnya.
   *
   * Dibandingkan dengan `wadahRef`, bukan sekadar `!== null`: orang yang menekan
   * F11 sendiri membuat layar penuh milik **dokumen**, dan halaman ini tidak
   * berhak mengaku itu miliknya.
   */
  const [penuh, setPenuh] = useState(false);
  useEffect(() => {
    const onUbah = () => setPenuh(document.fullscreenElement === wadahRef.current);
    document.addEventListener('fullscreenchange', onUbah);
    return () => document.removeEventListener('fullscreenchange', onUbah);
  }, []);

  /* ── Bilah kendali di dalam gambar (§G10.3) ───────────────────────────── */

  /**
   * Bilahnya menghilang saat ditinggalkan, seperti pemutar mana pun — tapi
   * **hanya ketika videonya sedang berjalan**. Bilah yang menghilang di atas
   * gambar yang juga diam adalah bilah yang pergi tanpa alasan: tidak ada yang
   * sedang ditonton, jadi tidak ada yang sedang terhalang.
   */
  const [kendaliTampil, setKendaliTampil] = useState(true);

  /**
   * Cermin state, plus tiga syarat yang menahannya — semuanya di `ref`.
   *
   * Semuanya dibaca dari dalam penangan `pointermove` yang berjalan puluhan
   * kali per detik, dan tidak satu pun perlu memicu render. Dengan begini
   * menggerakkan tetikus di atas gambar tidak merender ulang halaman terberat
   * di aplikasi ini sama sekali; yang merender cuma pergantian tampil ⇄
   * sembunyi, dua kali per kunjungan.
   */
  const tampilRef = useRef(true);
  const berjalanRef = useRef(false);
  const hoverBilahRef = useRef(false);
  const fokusBilahRef = useRef(false);
  const sembunyiRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  berjalanRef.current = main || mundur > 0;

  const setTampil = useCallback((v: boolean) => {
    if (tampilRef.current === v) return;
    tampilRef.current = v;
    setKendaliTampil(v);
  }, []);

  /**
   * Panggil kembali bilahnya, lalu jadwalkan kepergiannya.
   *
   * Tiga hal menahannya di layar, dan ketiganya berarti "masih ada yang sedang
   * memegangnya": videonya sedang diam, tetikus sedang berada di atas bilahnya,
   * atau fokus papan ketik ada di dalamnya. Yang terakhir bukan kemewahan —
   * menyembunyikan kendali yang sedang dipegang Tab berarti fokus mendarat di
   * sesuatu yang tidak kelihatan, dan orang yang memakai papan ketik kehilangan
   * jejak posisinya sama sekali.
   */
  const bangunkanKendali = useCallback(() => {
    setTampil(true);
    if (sembunyiRef.current) clearTimeout(sembunyiRef.current);
    sembunyiRef.current = null;
    if (
      !berjalanRef.current ||
      hoverBilahRef.current ||
      fokusBilahRef.current ||
      // Playhead yang sedang dipegang adalah alasan keempat, dan yang paling
      // sering kena: menyeret pelan lalu berhenti sejenak membuat bilahnya
      // hilang **di bawah jari yang sedang memegangnya**.
      menyeret.current
    ) {
      return;
    }
    sembunyiRef.current = setTimeout(() => setTampil(false), 2600);
  }, [setTampil]);

  /**
   * Posisi penunjuk terakhir, dipakai menyaring gerakan yang bukan gerakan.
   *
   * Chrome menyiarkan `pointermove` **sintetis** setiap kali isi di bawah kursor
   * berubah tanpa kursornya bergerak — dan bilah yang baru saja menghilang (jadi
   * `pointer-events: none`) adalah perubahan seperti itu. Tanpa saringan ini,
   * menghilangnya bilah membangunkan dirinya sendiri, lalu menghilang lagi 2,6
   * detik kemudian, selamanya. Paling kelihatan di layar penuh, tempat bilahnya
   * membesar dan kursor hampir selalu berhenti di dalam kotaknya.
   */
  const titikRef = useRef<{ x: number; y: number } | null>(null);
  const gerakDiGambar = useCallback(
    (e: React.PointerEvent) => {
      const t = titikRef.current;
      if (t && t.x === e.clientX && t.y === e.clientY) return;
      titikRef.current = { x: e.clientX, y: e.clientY };
      bangunkanKendali();
    },
    [bangunkanKendali],
  );

  /**
   * Playhead mulai / selesai dipegang — dari bilah di dalam gambar maupun dari
   * timeline di bawah halaman. Selama dipegang, bilahnya menetap dan posisi
   * playhead tidak boleh ditimpa video (lihat `onTimeUpdate`).
   */
  const tandaiSeret = useCallback(
    (v: boolean) => {
      menyeret.current = v;
      if (v) {
        setTampil(true);
        if (sembunyiRef.current) clearTimeout(sembunyiRef.current);
        sembunyiRef.current = null;
        return;
      }
      bangunkanKendali();
    },
    [setTampil, bangunkanKendali],
  );

  // Mulai atau berhenti memutar mengubah jawabannya: bilah yang tadi menetap
  // karena videonya diam harus mulai menghitung mundur begitu videonya jalan,
  // dan sebaliknya harus batal menghilang begitu videonya dijeda.
  useEffect(() => {
    bangunkanKendali();
    return () => {
      if (sembunyiRef.current) clearTimeout(sembunyiRef.current);
    };
  }, [main, mundur, bangunkanKendali]);

  /**
   * Fokus yang **terlihat**, bukan sekadar fokus.
   *
   * Mengklik tombol ⏵ juga memberinya fokus di sebagian besar browser. Kalau
   * fokus apa pun menahan bilahnya, satu klik pada Play berarti bilah yang
   * tidak pernah pergi lagi sepanjang tontonan — persis kebalikan dari yang
   * diminta. `:focus-visible` adalah pembeda yang sudah dipakai browser untuk
   * pertanyaan yang sama: fokus ini datang dari papan ketik atau dari tetikus?
   */
  function fokusPapanKetik(el: HTMLElement): boolean {
    try {
      return el.matches(':focus-visible');
    } catch {
      // Browser tanpa `:focus-visible` — anggap saja papan ketik. Bilah yang
      // menetap terlalu lama jauh lebih murah daripada fokus yang hilang.
      return true;
    }
  }

  /* ── Papan ketik §G10.1 ────────────────────────────────────────────────── */

  // Sejalan dengan penjaga `if (!timeline) return` di dalam penangan: tanpa
  // timeline, Kiri/Kanan tidak menggeser apa pun dan tidak perlu diklaim.
  usePanahTv(
    useCallback(
      (arah: ArahPanah) => {
        if (!timeline) return false;
        if (arah !== 'left' && arah !== 'right') return false;
        // Cermin penjaga di dalam penangan: di kolom isian, panah milik kursor teks.
        const t = document.activeElement;
        return !(t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName));
      },
      [timeline],
    ),
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Shortcut mati saat fokus di dalam input: mengetik "13:41" di kotak waktu
      // tidak boleh menjalankan mark in (§G10.1).
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName)) return;
      /**
       * `Alt`+`I` / `Alt`+`O` — buang mark in / mark out **satu per satu**.
       *
       * Diminta lapangan 8 September 2026. `Esc` sudah lama membuang keduanya
       * sekaligus, dan itu terlalu tumpul untuk perbuatan yang paling sering
       * dilakukan orang saat menyiapkan ekspor: memasang `O` di tempat yang
       * salah, lalu ingin mengulangi yang itu saja tanpa kehilangan `I` yang
       * sudah susah dicari.
       *
       * Diperiksa **sebelum** penjaga modifier di bawah, karena penjaga itu
       * memang membuang seluruh kombinasi ber-Alt — dan harus tetap begitu:
       * `Alt`+huruf lain milik menu peramban dan pintasan sistem.
       */
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key === 'i' || e.key === 'I') {
          e.preventDefault();
          setMarkIn(null);
          return;
        }
        if (e.key === 'o' || e.key === 'O') {
          e.preventDefault();
          setMarkOut(null);
          return;
        }
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (!timeline) return;

      const p = posMs;
      const geser = (ms: number) => p !== null && seek(p + ms);

      switch (e.key) {
        case ' ':
          e.preventDefault();
          main || mundur ? jeda() : putar();
          return;
        case 'l':
        case 'L':
          e.preventDefault();
          majuLebihCepat();
          return;
        case 'j':
        case 'J':
          e.preventDefault();
          mundurLebihCepat();
          return;
        case 'k':
        case 'K':
          e.preventDefault();
          setRate(1);
          jeda();
          return;
        case 'ArrowRight':
          e.preventDefault();
          geser(e.shiftKey ? 1000 : FRAME_MS);
          return;
        case 'ArrowLeft':
          e.preventDefault();
          geser(e.shiftKey ? -1000 : -FRAME_MS);
          return;
        case '+':
        case '=':
          e.preventDefault();
          setView((v) => (v && p !== null ? zoom(v, 0.6, p, batasSumbu.min, batasSumbu.max) : v));
          return;
        case '-':
        case '_':
          e.preventDefault();
          setView((v) => (v && p !== null ? zoom(v, 1.7, p, batasSumbu.min, batasSumbu.max) : v));
          return;
        case 'i':
          e.preventDefault();
          if (p !== null) setMarkIn(p);
          return;
        case 'o':
          e.preventDefault();
          if (p !== null) setMarkOut(p);
          return;
        case 'I':
          e.preventDefault();
          if (markIn !== null) seek(markIn);
          return;
        case 'O':
          e.preventDefault();
          if (markOut !== null) seek(markOut);
          return;
        case 'm':
        case 'M':
          e.preventDefault();
          /**
           * `M` membuka kotak label; ia **tidak** langsung menyimpan penanda
           * tanpa nama (§D8.7 no.1). Video sengaja tidak dijeda: menjeda
           * pemutaran untuk mencatat apa yang sedang terjadi berarti berhenti
           * melihat kejadiannya (§D8.7 no.2).
           */
          if (p !== null) {
            setTandaBaru({ atMs: p });
            setLabelBaru('');
            setBookmarkError(null);
          }
          return;
        case 'n':
          e.preventDefault();
          if (p !== null) {
            const next = waktuBookmark.find((m) => m > p + 1000);
            if (next !== undefined) seek(next);
          }
          return;
        case 'N':
          e.preventDefault();
          if (p !== null) {
            const prev = [...waktuBookmark].reverse().find((m) => m < p - 1000);
            if (prev !== undefined) seek(prev);
          }
          return;
        case 'f':
        case 'F':
          e.preventDefault();
          layarPenuh();
          return;
        case 'Escape':
          setMarkIn(null);
          setMarkOut(null);
          return;
        /**
         * Home / End — **relatif terhadap jendela, bukan terhadap arsip**
         * (§G10.11 poin 6).
         *
         * Diminta lapangan: *"HOME pertama membawa head ke ujung kiri timeline
         * yang sedang terlihat. HOME berikutnya menggeser timeline satu layar
         * ke kiri dan head ikut berada di ujung kiri. (…) Jangan langsung
         * teleport ke awal/akhir seluruh recording."*
         *
         * Bentuk lamanya — lompat ke ujung retensi — punya satu masalah yang
         * tidak bisa diperbaiki: orang yang menekannya sedang memeriksa satu
         * jam tertentu, dan tombol yang membuang seluruh konteks itu praktis
         * cuma dipakai sekali sebelum tidak pernah disentuh lagi. Yang di sini
         * membalik keduanya: tekanan pertama **membingkai** apa yang sudah
         * terlihat, tekanan berikutnya **membalik halaman** — persis kelakuan
         * PageUp/PageDown di dokumen, tapi pada sumbu waktu.
         *
         * Perbandingan "sudah di tepi?" memakai `daratSeek`, bukan `view.from`
         * mentah: kalau tepi jendela jatuh di dalam bolong rekaman, playhead
         * mendarat di awal segmen berikutnya, dan membandingkannya dengan tepi
         * akan selalu berkata "belum sampai" — Home yang tidak pernah bisa
         * membalik halaman.
         */
        /**
         * `PgUp` / `PgDn` — pindah **potongan**, bukan geser sekian detik.
         *
         * Diminta lapangan 8 September 2026 bersamaan dengan dibuangnya panah
         * di atas gambar. Arahnya **dibalik pada hari yang sama** sesudah
         * dicoba di layar: *"kayaknya secara kenyamanan, pg up dan pg down
         * kebalik deh. pg up = prev, pg down = next."* — dan yang sekarang
         * memang yang searah dengan kebiasaan menggulung dokumen: PgDn turun,
         * yaitu maju.
         *
         * `lompatSegmen` berpindah berkas, dan pada rekaman yang berbolong
         * potongan berikutnya bisa saja tiga jam kemudian — itu justru gunanya
         * (§G10.5).
         */
        case 'PageUp':
          e.preventDefault();
          lompatSegmen(-1);
          return;
        case 'PageDown':
          e.preventDefault();
          lompatSegmen(1);
          return;
        case 'Home':
        case 'End': {
          e.preventDefault();
          if (!view) return;
          const lebarJendela = view.to - view.from;
          // Toleransi kecil: playhead yang sedang berjalan bergerak beberapa
          // milidetik antara dua tekanan, dan tanpa kelonggaran ini tekanan
          // kedua terbaca sebagai "belum di tepi".
          const toleransi = Math.max(500, lebarJendela * 0.001);
          const keKiri = e.key === 'Home';
          const tepi = keKiri ? view.from : view.to;
          const darat = daratSeek(tepi);
          const sudahDiTepi =
            p !== null &&
            darat !== null &&
            (keKiri ? p <= darat.ms + toleransi : p >= darat.ms - toleransi);

          if (!sudahDiTepi) {
            seek(tepi);
            return;
          }

          // Satu layar penuh, dijepit batas keras sumbu. Kalau sudah mentok,
          // `from` tidak berubah dan yang tersisa cuma seek ke tepi yang sama —
          // benar, dan itu memang ujung yang sesungguhnya.
          let from = keKiri
            ? Math.max(batasSumbu.min, view.from - lebarJendela)
            : Math.min(batasSumbu.max - lebarJendela, view.from + lebarJendela);
          from = Math.min(Math.max(from, batasSumbu.min), Math.max(batasSumbu.min, batasSumbu.max - lebarJendela));
          setView({ from, to: from + lebarJendela });
          seek(keKiri ? from : from + lebarJendela);
          return;
        }
        case '?':
          setBantuan((v) => !v);
          return;
        default:
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [timeline, posMs, main, mundur, markIn, markOut, waktuBookmark, seek, daratSeek, view, batasSumbu, putar, jeda, majuLebihCepat, mundurLebihCepat, lompatSegmen, fromMs, toMs, layarPenuh]);

  /* ── Unduhan §D8.2 ─────────────────────────────────────────────────────── */

  const rentangUnduh = useMemo(() => {
    if (markIn !== null && markOut !== null && markOut > markIn) return { dari: markIn, sampai: markOut };
    if (fromMs === null || toMs === null) return null;
    return { dari: fromMs, sampai: toMs };
  }, [markIn, markOut, fromMs, toMs]);

  const durasiUnduhS = rentangUnduh ? Math.round((rentangUnduh.sampai - rentangUnduh.dari) / 1000) : 0;

  /**
   * Bolong yang ikut terpotong. §D8.2 mewajibkan ini disebut sebelum orang
   * mengira berkas hasilnya utuh: concat menyambung apa yang ada, jadi
   * durasinya lebih pendek daripada rentang jamnya.
   */
  const bolongTerpilih = useMemo(() => {
    if (!timeline || !rentangUnduh) return 0;
    return timeline.gaps.reduce((total, g) => {
      const a = Math.max(new Date(g.startedAt).getTime(), rentangUnduh.dari);
      const b = Math.min(new Date(g.endedAt).getTime(), rentangUnduh.sampai);
      return total + Math.max(0, b - a);
    }, 0);
  }, [timeline, rentangUnduh]);

  const mintaUnduhan = useCallback(async () => {
    if (!cameraId || !rentangUnduh) return;
    setUnduhError(null);
    try {
      const job = await api<ExportJob>('/exports', {
        method: 'POST',
        body: JSON.stringify({
          camera_id: cameraId,
          from: new Date(rentangUnduh.dari).toISOString(),
          to: new Date(rentangUnduh.sampai).toISOString(),
          // Kosong = nama bawaan `{nama_kamera}_{YYYYMMDD_HHMMSS}.mp4` (§D8.8
          // no.2) — bukan "export-12.mp4": orang yang tidak mengisi apa pun
          // tetap berhak mendapat berkas yang bisa dikenali di folder Unduhan.
          name: namaExport.trim() || null,
        }),
      });
      setUnduhan(job);
    } catch (e) {
      setUnduhError((e as Error).message);
    }
  }, [cameraId, rentangUnduh, namaExport]);

  /**
   * Polling status (§G10.8). Berhenti sendiri begitu selesai — tidak ada
   * interval yang terus berdetak setelah berkasnya jadi.
   *
   * Lajunya dibedakan: **2 detik selama `running`**, 10 detik kalau masih
   * `queued`. Antrean yang menunggu tidak berubah tiap dua detik, dan polling
   * yang terus berjalan di halaman yang dibuka sepanjang hari adalah beban tetap
   * untuk jawaban yang hampir selalu sama.
   */
  useEffect(() => {
    if (!unduhan) return;
    if (unduhan.status !== 'queued' && unduhan.status !== 'running') return;
    const jeda = unduhan.status === 'running' ? 2000 : 10_000;
    const timer = setInterval(() => {
      void api<ExportJob>(`/exports/${unduhan.id}`)
        .then(setUnduhan)
        .catch((e) => setUnduhError((e as Error).message));
    }, jeda);
    return () => clearInterval(timer);
  }, [unduhan]);

  /**
   * Begitu berkasnya siap, unduhannya **jalan sendiri**.
   *
   * Sebelumnya orang harus menekan "Download", menunggu, lalu menekan "Save
   * file" — dua tombol untuk satu maksud, dan yang kedua muncul di tempat yang
   * berbeda dari yang pertama. Tombol pertama sudah menyatakan niatnya; menunggu
   * berkasnya dirakit bukan alasan untuk menanyakannya lagi.
   *
   * Dikerjakan lewat `<a download>` sintetis, bukan `location.href`: mengganti
   * lokasi halaman pada respons `attachment` memang tidak menavigasi, tapi ia
   * membatalkan permintaan `<video>` yang sedang berjalan di sebagian browser —
   * dan yang terlihat pemakai adalah pemutar yang mati tepat saat unduhannya
   * mulai.
   *
   * `sudahDiunduh` menjaganya sekali per pekerjaan: efek ini ikut berjalan lagi
   * setiap kali `unduhan` di-set ulang oleh polling, dan tanpa penjaga itu satu
   * export bisa memicu beberapa unduhan sekaligus.
   */
  const sudahDiunduh = useRef<string | null>(null);
  useEffect(() => {
    if (!unduhan || unduhan.status !== 'ready') return;
    if (sudahDiunduh.current === unduhan.id) return;
    sudahDiunduh.current = unduhan.id;

    const a = document.createElement('a');
    a.href = `/api/v1/exports/${unduhan.id}/download`;
    // Nama berkasnya ditentukan server lewat Content-Disposition (§F6) — atribut
    // ini cuma menyatakan "unduh, jangan buka di tab".
    a.download = '';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [unduhan]);

  /* ── Tata letak & pemilih (§G10.4, revisi lapangan 31 Agu 2026) ───────── */

  /**
   * Isian jam mengikuti jendela yang **terlihat**.
   *
   * "Dari jam berapa sampai jam berapa" bagi orang yang memakainya adalah apa
   * yang sedang ia lihat di timeline, bukan rentang yang kebetulan diketik
   * sepuluh gulungan yang lalu. Menyambungkan keduanya membuat tombol Show,
   * kotak unduhan, dan bayangan di timeline semuanya berbicara tentang hal yang
   * sama — dan menghapus satu kelas kebingungan sendiri: rentang unduhan yang
   * diam-diam jauh lebih lebar daripada yang di layar.
   */
  useEffect(() => {
    if (!view) return;
    setFromInput(toLocalInput(new Date(view.from)));
    setToInput(toLocalInput(new Date(view.to)));
  }, [view]);

  /**
   * Bilah pemilih di atas halaman bisa dilipat.
   *
   * Di ponsel bilahnya wajar; di monitor PC ia memakan 96px tinggi yang
   * seluruhnya diambil dari gambar — dan gambar adalah satu-satunya alasan
   * halaman ini dibuka. Dilipat, yang tersisa satu baris ringkas yang tetap
   * menyebut kamera dan rentang yang sedang dilihat, jadi tidak ada informasi
   * yang hilang bersama tingginya. Pilihannya diingat: orang yang melipatnya
   * sekali tidak sedang mengatur satu kunjungan, ia sedang menyatakan
   * kebiasaan.
   */
  const [bilahTampil, setBilahTampil] = useState(() => {
    try {
      return localStorage.getItem('lensa.playback.bilah') !== 'tutup';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('lensa.playback.bilah', bilahTampil ? 'buka' : 'tutup');
    } catch {
      // Mode privat / storage dimatikan — pilihannya cukup berlaku sesi ini.
    }
  }, [bilahTampil]);

  /**
   * Panel tepi kanan gambar: daftar klip (§G10.5) atau daftar bookmark
   * (§G10.7). Satu tempat, dua tab — keduanya ikut masuk ke layar penuh, dan
   * tidak ada kotak baru yang memakan tinggi gambar (§G7 no.1).
   */
  const [panel, setPanel] = useState<'klip' | 'bookmark' | null>(null);

  /**
   * Panel "Lainnya" (§G10 poin 4) — popover di desktop, lembar bawah di ponsel.
   *
   * Sebelas kendali dulu berbaris permanen di bawah timeline: mark in, mark
   * out, hapus tanda, klip, bookmark, jam, jumlah bolong, nama berkas export,
   * unduh, dan bantuan. Di laptop 13" barisnya membungkus jadi dua, dan baris
   * kedua itu diambil dari tinggi gambar — barang paling mahal di halaman ini.
   *
   * Yang tinggal di layar cuma yang dipakai setiap kali: transport, tanda, dan
   * Download. Sisanya tetap ada, tetap sama perilakunya, cuma tidak lagi
   * menempati layar sepanjang waktu.
   */
  const [lainnya, setLainnya] = useState(false);

  /**
   * Zoom digital di gambar rekaman (§G10.6) — gestur yang sama persis dengan
   * layar penuh dashboard (§G8.2), dari modul yang sama.
   *
   * Kendali yang duduk **di atas** gambar dilewatkan: bilah pemutar, tombol
   * layar penuh, dan daftar klip. Tanpa itu, menekan ⏵ dua kali cepat terbaca
   * sebagai ketuk ganda dan gambarnya ikut membesar — dan menggulung daftar
   * klip akan men-zoom videonya alih-alih menggulung daftarnya.
   */
  const lewatiKendali = useCallback(
    (t: EventTarget | null) => t instanceof Element && t.closest('[data-kendali]') !== null,
    [],
  );
  const {
    zoom: zoomGambar,
    gaya: gayaZoom,
    reset: resetZoom,
    handlers: zoomHandlers,
  } = useZoomGambar(wadahRef, { lewati: lewatiKendali });

  // Zoom milik kamera yang sedang dilihat: 3× di pojok kiri atas kamera lorong
  // tidak berarti apa-apa di kamera gerbang.
  useEffect(() => {
    resetZoom();
  }, [cameraId, resetZoom]);

  /**
   * Keluar dari layar penuh mengembalikan gambar ke 1× (31 Agustus 2026,
   * dilaporkan lapangan: *"kalo lg fullscreen dan zoom in trus keluar, videonya
   * jd hitam karena zoomnya ga balik"*).
   *
   * Hitamnya sendiri sudah tidak mungkin terjadi lagi — `useZoomGambar`
   * menskalakan ulang geserannya begitu kotaknya berubah ukuran, dan itu
   * perbaikan yang berlaku di mana pun kotaknya berubah. Yang tersisa adalah
   * pertanyaan berikutnya: sesudah menyusut, gambar 3× di kotak sepertiga
   * ukurannya menyisakan sangat sedikit konteks, dan orang yang menekan tombol
   * "keluar layar penuh" memang sedang meminta tampilan utuhnya kembali.
   *
   * Sengaja hanya satu arah. **Masuk** ke layar penuh mempertahankan zoomnya:
   * di sana perbesarannya justru baru mulai berguna, dan membatalkannya berarti
   * mencari ulang titik yang sedang diperhatikan orang.
   */
  const penuhSebelumnya = useRef(false);
  useEffect(() => {
    if (penuhSebelumnya.current && !penuh) resetZoom();
    penuhSebelumnya.current = penuh;
  }, [penuh, resetZoom]);

  /**
   * Pemilih kamera: kotak ketik + daftar yang menyusut, bukan `<select>`.
   *
   * Dengan 94 kamera, `<select>` berarti menggulung daftar sepanjang layar
   * untuk menemukan satu nama — dan namanya sendiri sering tidak diingat persis.
   * Kotak ini mencari nama, host, DAN path lewat `cocokKamera`, sama persis
   * dengan kotak di `/cameras` dan di bilah dashboard: kotak yang berbunyi sama
   * wajib mencari hal yang sama.
   */
  const [cariKam, setCariKam] = useState('');
  const [bukaKam, setBukaKam] = useState(false);
  const [sorotKam, setSorotKam] = useState(0);
  const kotakKamRef = useRef<HTMLDivElement>(null);

  const hasilKamera = useMemo(
    () => (cameras ?? []).filter((c) => cocokKamera(c, cariKam.trim())).slice(0, 60),
    [cameras, cariKam],
  );

  // Saat tidak sedang dicari, kotaknya menampilkan kamera yang sedang dibuka —
  // ia label sekaligus kotak pencarian, dan label yang salah lebih buruk
  // daripada kotak kosong.
  useEffect(() => {
    if (!bukaKam) setCariKam(kamera?.name ?? '');
  }, [kamera, bukaKam]);

  useEffect(() => {
    if (!bukaKam) return;
    const onLuar = (e: PointerEvent) => {
      if (!kotakKamRef.current?.contains(e.target as Node)) setBukaKam(false);
    };
    document.addEventListener('pointerdown', onLuar);
    return () => document.removeEventListener('pointerdown', onLuar);
  }, [bukaKam]);

  const pilihKamera = useCallback(
    (id: string) => {
      setBukaKam(false);
      navigate(`/videos/${id}`);
    },
    [navigate],
  );

  /* ── Render ────────────────────────────────────────────────────────────── */

  const rentangTerlaluPanjang = durasiUnduhS > RENTANG_UNDUH_MAX_S;

  const { user } = useAuth();
  const staf = isStaff(user);
  const layarLebar = useLayarLebar();

  /**
   * Tinggi kotak gambar dan pita timeline, diatur sendiri lewat garis pembatas
   * — desktop saja (Radian, 6 Okt 2026). `video: null` = bawaan: gambar
   * mengisi sisa layar. Diisi = tinggi tetap, dan halamannya boleh menggulung
   * supaya gambar bisa dibesarkan sampai selebar kotaknya (tanpa pita hitam
   * kiri-kanan). Diingat per browser.
   */
  const [ukuranPanel, setUkuranPanel] = useState<{ video: number | null; pita: number }>(() => {
    try {
      const v = JSON.parse(localStorage.getItem('lensa.playback.ukuran') ?? 'null') as {
        video?: unknown;
        pita?: unknown;
      } | null;
      return {
        video: typeof v?.video === 'number' ? v.video : null,
        pita: typeof v?.pita === 'number' ? v.pita : TINGGI_PITA,
      };
    } catch {
      return { video: null, pita: TINGGI_PITA };
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('lensa.playback.ukuran', JSON.stringify(ukuranPanel));
    } catch {
      // Mode privat / storage dimatikan — ukurannya cukup berlaku sesi ini.
    }
  }, [ukuranPanel]);
  const aturUkuran = layarLebar && !penuh;
  const tinggiVideo = aturUkuran ? ukuranPanel.video : null;
  const tinggiPita = aturUkuran ? ukuranPanel.pita : TINGGI_PITA;

  /**
   * Zoom timeline lewat tombol, bukan cuma roda tetikus dan cubitan.
   *
   * Keduanya sudah ada dan tidak diubah (§G10.6). Yang ditambah jalan ketiga:
   * di layar sentuh tanpa cubitan yang enak — dan di tetikus tanpa roda —
   * timeline praktis terkunci pada rentang yang kebetulan dimuat. Tombol
   * −/+ adalah kendali yang bisa ditemukan tanpa diberitahu.
   *
   * Berputar di sekitar **playhead** kalau ia ada di dalam jendela: yang sedang
   * diperiksa orang posisi itu, dan zoom yang memusat di tengah jendela akan
   * membuangnya ke luar layar pada tarikan kedua.
   */
  const zoomSumbu = useCallback(
    (faktor: number) => {
      setView((v) => {
        if (!v) return v;
        const rentang = v.to - v.from;
        const penuhSumbu = batasSumbu.max - batasSumbu.min;
        const pusat = posMs !== null && posMs >= v.from && posMs <= v.to ? posMs : (v.from + v.to) / 2;
        // Lantai satu menit: di bawah itu penggarisnya kehabisan angka untuk
        // ditulis dan seretan playhead jadi lebih halus daripada satu piksel.
        const baru = Math.max(60_000, Math.min(penuhSumbu, rentang * faktor));
        let from = pusat - (pusat - v.from) * (baru / rentang);
        let to = from + baru;
        if (from < batasSumbu.min) {
          from = batasSumbu.min;
          to = from + baru;
        }
        if (to > batasSumbu.max) {
          to = batasSumbu.max;
          from = to - baru;
        }
        return { from, to };
      });
    },
    [posMs, batasSumbu],
  );

  /**
   * Geser jendela timeline sekian milidetik, tanpa mengubah lebarnya.
   *
   * Dipakai tombol `‹`/`›` di strip pratinjau. Lebar jendela sengaja
   * dipertahankan persis: strip yang menggeser sambil diam-diam mengubah skala
   * akan membuat kotak yang barusan dilihat orang berubah arti di tengah jalan.
   */
  const geserJendela = useCallback(
    (deltaMs: number) => {
      setView((v) => {
        if (!v) return v;
        const rentang = v.to - v.from;
        let from = v.from + deltaMs;
        from = Math.min(Math.max(from, batasSumbu.min), Math.max(batasSumbu.min, batasSumbu.max - rentang));
        return { from, to: from + rentang };
      });
    },
    [batasSumbu],
  );

  /**
   * Jendela timeline menyusul playhead yang keluar dari layar.
   *
   * Dilaporkan lapangan 8 Sep 2026: *"kalo gw closeup terlalu dalem, terus
   * videoheadnya jalan terus, ilang dari timeline"*. Benar — sebelum ini
   * jendela hanya berubah kalau orangnya sendiri yang menggeser, jadi zoom
   * dalam berarti playhead berjalan keluar layar dan tidak pernah kembali.
   *
   * Tiga pagar supaya ia tidak melawan tangan yang sedang memegang:
   *
   * 1. **Hanya saat berjalan.** Video yang dijeda tidak boleh memindahkan
   *    jendela yang baru saja diletakkan orangnya di tempat tertentu.
   * 2. **Tidak selama diseret.** Menggeser timeline dengan playhead di luar
   *    layar adalah perbuatan yang sah — biasanya justru sedang mencari tempat
   *    lain untuk dituju.
   * 3. **Digeser, bukan di-zoom ulang.** Lebar jendela dipertahankan persis;
   *    yang berubah cuma posisinya. Zoom adalah keputusan orangnya, dan
   *    mengembalikannya diam-diam adalah cara tercepat membuat orang berhenti
   *    memakai zoom sama sekali.
   *
   * Playhead didaratkan di seperempat kiri, bukan di tengah: yang belum
   * ditonton ada di sebelah kanannya, dan di situlah ruang layar lebih berguna.
   */
  useEffect(() => {
    if (posMs === null || !view) return;
    if (!main && mundur === 0) return;
    if (menyeret.current) return;

    const rentang = view.to - view.from;
    // Mulai menggeser sedikit SEBELUM playhead benar-benar menyentuh tepi —
    // menunggu sampai ia keluar berarti ada sekejap ketika ia memang hilang.
    const tepi = rentang * 0.08;
    if (posMs <= view.to - tepi && posMs >= view.from) return;

    let from = posMs - rentang * 0.25;
    let to = from + rentang;
    if (from < batasSumbu.min) {
      from = batasSumbu.min;
      to = from + rentang;
    }
    if (to > batasSumbu.max) {
      to = batasSumbu.max;
      from = to - rentang;
    }
    setView({ from, to });
  }, [posMs, view, main, mundur, batasSumbu]);

  /** Kotak "Go to time" — tertutup sampai diminta. */
  const [keJam, setKeJam] = useState<string | null>(null);

  /**
   * **Satu kelompok transport per layar, selalu.**
   *
   * Sampai hari ini keduanya hidup bersamaan di desktop: bilah di dalam gambar
   * (§G10.3) muncul saat penunjuk mendekat, dan kelompok transport di bawah
   * timeline selalu ada — dua ⏵, dua ⏪, dan **dua penunjuk laju** yang
   * kadang-kadang menampilkan angka berbeda karena yang satu belum sempat
   * dirender ulang. Itu keluhan nomor satu tentang halaman ini.
   *
   * Aturannya sekarang saling meniadakan, bukan salah satu dibuang: di layar
   * penuh dan di ponsel bilah dalam gambar adalah **satu-satunya** kendali yang
   * bisa dijangkau (di layar penuh transport halaman tertinggal di belakang
   * layar penuh; di ponsel tidak ada ruang untuk dua baris), jadi di sana ia
   * yang tampil dan transport halaman padam. Di desktop biasa kebalikannya.
   */
  const bilahDalamGambar = penuh || !layarLebar;

  return (
    /* Halaman ini satu layar penuh, bukan halaman yang menggulung (§G10.4).
       Gambar mengambil semua tinggi yang tersisa, dan timeline dikunci tepat di
       bawahnya — itu permintaan lapangan 31 Agu 2026: *"bagian atasnya bisa
       di-expand dan collapse biar layar frame bisa lebih gede, terus
       timeline-nya selalu di lock di bagian bawah frame, dan framenya
       menyesuaikan layarnya"*. Sebelumnya tinggi gambar dihitung dari sebuah
       angka tetap (31,25rem) yang harus ditebak ulang setiap kali ada yang
       ditambah di bawahnya — dan setiap tebakan yang meleset berakhir sebagai
       gambar kekecilan atau timeline yang terpotong di bawah lipatan. */
    <div
      className={`flex h-full min-h-0 flex-col gap-2 overflow-x-clip p-3 ${
        tinggiVideo !== null ? 'overflow-y-auto' : ''
      }`}
    >
      <h1 className="sr-only">{t('Recordings')}</h1>

      {bilahTampil ? (
        <div className="flex shrink-0 flex-wrap items-end gap-3 rounded-lg border border-line bg-surface p-3">
          {/* Pemilih kamera: kotak ketik, bukan `<select>` sepanjang 94 baris. */}
          <div ref={kotakKamRef} className="relative flex flex-col gap-1 text-sm">
            <label htmlFor="cari-kamera">{t('Camera')}</label>
            <input
              id="cari-kamera"
              type="text"
              role="combobox"
              aria-expanded={bukaKam}
              aria-controls="daftar-kamera"
              aria-autocomplete="list"
              autoComplete="off"
              value={cariKam}
              placeholder={t('Search Name, Host or Path')}
              onFocus={() => {
                setBukaKam(true);
                setCariKam('');
                setSorotKam(0);
              }}
              onChange={(e) => {
                setCariKam(e.target.value);
                setBukaKam(true);
                setSorotKam(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setBukaKam(true);
                  setSorotKam((i) => Math.min(i + 1, hasilKamera.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSorotKam((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const c = hasilKamera[sorotKam];
                  if (c) pilihKamera(c.id);
                } else if (e.key === 'Escape') {
                  setBukaKam(false);
                }
              }}
              className="min-h-tap w-64 rounded-md border border-line bg-surface px-2"
            />
            {bukaKam && (
              <ul
                id="daftar-kamera"
                role="listbox"
                aria-label={t('Camera')}
                className="absolute left-0 top-full z-30 mt-1 max-h-72 w-80 max-w-[85vw] overflow-y-auto rounded-md border border-line bg-surface shadow-lg"
              >
                {hasilKamera.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-muted">
                    {t('No camera matches “{cari}”.', { cari: cariKam.trim() })}
                  </li>
                ) : (
                  hasilKamera.map((c, i) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={c.id === cameraId}
                        onPointerEnter={() => setSorotKam(i)}
                        onClick={() => pilihKamera(c.id)}
                        className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left ${
                          i === sorotKam ? 'bg-subtle' : ''
                        }`}
                      >
                        {/* Cuma namanya. Host dan path tetap ikut **dicari**
                            (`cocokKamera`), tapi tidak ikut ditampilkan: yang
                            sedang dikerjakan orang di sini memilih kamera, dan
                            alamat IP di setiap baris cuma kebisingan yang
                            membuat daftar dua kali lebih tinggi — diminta
                            lapangan 31 Agu 2026. */}
                        <span className={`text-sm ${c.id === cameraId ? 'font-semibold text-accent-text' : ''}`}>
                          {c.name}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {/* Tanggal — satu kotak, bukan dua kali "tanggal + jam". Jamnya
              diurus chip di sebelahnya. */}
          <div className="flex flex-col gap-1 text-sm">
            <label htmlFor="tanggal-playback">{t('Date')}</label>
            <div className="flex items-center gap-2">
              <input
                id="tanggal-playback"
                type="date"
                value={tanggal}
                max={toDateInput(new Date())}
                /* Tidak bisa mundur lebih jauh dari retensi (§D6): tanggal yang
                   dijamin kosong bukan pilihan, cuma layar kosong yang harus
                   dijelaskan lagi. */
                min={toDateInput(new Date(Date.now() - RETENSI_MAX_HARI * 86400_000))}
                onChange={(e) => terapkanRentang(rentangCepat ?? '24H', e.target.value)}
                className="min-h-tap rounded-md border border-line bg-surface px-2"
              />
              <button
                type="button"
                onClick={() => terapkanRentang(rentangCepat ?? '1H', toDateInput(new Date()))}
                disabled={tanggal === toDateInput(new Date())}
                className="min-h-tap rounded-md border border-line px-3 text-sm hover:bg-subtle disabled:opacity-40"
              >
                {t('Today')}
              </button>
            </div>
          </div>

          {/* Rentang cepat. Satu klik untuk hampir semua kasus. */}
          <div className="flex flex-col gap-1 text-sm">
            <span id="label-rentang">{t('Quick range')}</span>
            <div className="flex items-center gap-1" role="group" aria-labelledby="label-rentang">
              {RENTANG_CEPAT.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  aria-pressed={rentangCepat === r.id}
                  onClick={() => terapkanRentang(r.id, tanggal)}
                  className={`min-h-tap min-w-11 rounded-md px-3 text-sm font-medium tabular-nums transition-[background-color,scale] duration-150 active:scale-[0.96] ${
                    rentangCepat === r.id
                      ? 'bg-accent text-accent-contrast'
                      : 'border border-line hover:bg-subtle'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <span className="self-end pb-2.5 text-xs text-muted">WIB</span>

          <button
            type="button"
            onClick={() => setFilterLain((v) => !v)}
            aria-expanded={filterLain}
            aria-controls="filter-lain"
            className={`ml-auto flex min-h-tap items-center gap-1.5 self-end rounded-md border px-3 text-sm ${
              filterLain ? 'border-accent text-accent-text' : 'border-line hover:bg-subtle'
            }`}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 5h18l-7 8v6l-4-2v-4z" />
            </svg>
            {t('More filters')}
          </button>

          {/* Pintu ke multi-kamera. Ditaruh di bilah filter, bukan di sidebar:
              yang membukanya orang yang sudah berada di sebuah kejadian dan
              baru saja bertanya "kelihatan dari kamera mana lagi?" — dan itu
              pertanyaan yang lahir di sini, bukan di navigasi.

              **Hanya di layar lebar.** Dilaporkan lapangan 8 Sep 2026: *"di
              mode hape kok ada mode multi camera ya? Harusnya kan nggak ada.
              Soalnya di mobile jelek banget kelihatannya."* — dan itu bukan
              soal rapi-rapian: empat gambar di atas satu kolom selebar 360px
              menyisakan tinggi ±90px per kamera, di bawah tinggi satu baris
              nama kameranya sendiri. Halaman itu menjanjikan perbandingan empat
              sudut pada satu detik yang sama; di ponsel ia tidak bisa
              menepatinya, jadi pintunya ditutup di sini dan halamannya sendiri
              menolak dengan sopan (lihat `MultiVideosPage`). */}
          {layarLebar && (
          <Link
            to="/videos/multi"
            className="flex min-h-tap items-center gap-1.5 self-end rounded-md border border-line px-3 text-sm hover:bg-subtle"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="3" width="7.5" height="7.5" rx="1" />
              <rect x="13.5" y="3" width="7.5" height="7.5" rx="1" />
              <rect x="3" y="13.5" width="7.5" height="7.5" rx="1" />
              <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1" />
            </svg>
            {t('Multi-camera')}
          </Link>
          )}

          <button
            type="button"
            onClick={() => setBilahTampil(false)}
            className="flex min-h-tap items-center gap-1 self-end rounded-md border border-line px-3 text-sm hover:bg-subtle"
            aria-expanded
            aria-controls="bilah-pemilih"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 15 6-6 6 6" />
            </svg>
            {t('Hide filters')}
          </button>

          {/* Jalur lama, utuh, di balik satu tombol (§G10 poin 1).
              Yang perlu jam persis — investigasi yang sudah tahu menitnya —
              tetap punya jalannya, dan chip di atas tidak berpura-pura mewakili
              angka yang diketik sendiri: begitu salah satu kotak ini disentuh,
              chipnya padam. */}
          {filterLain && (
            <div
              id="filter-lain"
              className="flex w-full flex-wrap items-end gap-3 border-t border-line pt-3"
            >
              <label className="flex flex-col gap-1 text-sm">
                {t('From')}
                <input
                  type="datetime-local"
                  value={fromInput}
                  onChange={(e) => {
                    setFromInput(e.target.value);
                    setRentangCepat(null);
                  }}
                  className="min-h-tap rounded-md border border-line bg-surface px-2"
                />
              </label>

              <label className="flex flex-col gap-1 text-sm">
                {t('To')}
                <input
                  type="datetime-local"
                  value={toInput}
                  onChange={(e) => {
                    setToInput(e.target.value);
                    setRentangCepat(null);
                  }}
                  className="min-h-tap rounded-md border border-line bg-surface px-2"
                />
              </label>

              <button
                type="button"
                onClick={() => void muat()}
                disabled={!cameraId || memuat}
                className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.96] disabled:opacity-40"
              >
                {memuat ? t('Loading…') : t('Show')}
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Terlipat: satu baris yang tetap menyebut kamera dan rentang yang
           sedang dilihat. Bilah yang dilipat menjadi tombol tanpa keterangan
           akan membuat orang membukanya lagi hanya untuk memastikan ia sedang
           melihat kamera yang benar. */
        <button
          type="button"
          id="bilah-pemilih"
          onClick={() => setBilahTampil(true)}
          aria-expanded={false}
          className="flex shrink-0 items-center gap-2 self-start rounded-lg border border-line bg-surface px-3 py-1.5 text-sm hover:bg-subtle"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
          <span className="font-medium">{kamera?.name ?? t('— select a camera —')}</span>
          {view && (
            <span className="tabular-nums text-muted">
              · {formatWibTanggal(view.from)} {formatWibJam(view.from)} – {formatWibJam(view.to)} WIB
            </span>
          )}
        </button>
      )}

      {error && (
        <p className="shrink-0 text-sm text-amber-600" role="alert">
          {error}
        </p>
      )}

      {/* Kesehatan kamera (§G10.2) ikut terlipat bersama bilahnya: ia jawaban
          atas "kameranya sehat tidak?", pertanyaan yang datang sekali di awal —
          bukan yang perlu menempel di layar selama menonton. */}
      {bilahTampil && cameraId && <CameraHealthBar cameraId={cameraId} />}

      {!cameraId ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-dashed border-line p-10 text-center">
          <p className="text-lg">{t('Choose a camera to see its recordings')}</p>
        </div>
      ) : timeline && timeline.segments.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-line p-10 text-center">
          <p className="text-lg">{t('No recordings in this range')}</p>
          <p className="mt-1 text-sm text-muted">
            {t('{nama} has no recordings between {dari} and {sampai}. Try widening the range.', {
              nama: kamera?.name ?? '',
              dari: formatWib(timeline.from),
              sampai: formatWib(timeline.to),
            })}
          </p>
        </div>
      ) : (
        <>
          {/* Gambar mengambil seluruh tinggi yang tersisa. Rasionya dijaga
              `object-contain`, bukan oleh kotaknya — kotak yang memaksakan 16:9
              justru yang dulu membuat gambar mengecil di layar lebar. */}
          <div
            ref={wadahRef}
            /* Seretan menurun di kotak gambar adalah geseran zoom, bukan
               tarik-untuk-muat-ulang (`lib/muatUlangAndroid.ts`). */
            data-tanpa-muat-ulang=""
            onPointerMove={(e) => {
              gerakDiGambar(e);
              zoomHandlers.onPointerMove(e);
            }}
            onPointerDown={(e) => {
              titikRef.current = { x: e.clientX, y: e.clientY };
              bangunkanKendali();
              zoomHandlers.onPointerDown(e);
            }}
            onPointerUp={zoomHandlers.onPointerUp}
            onPointerCancel={zoomHandlers.onPointerCancel}
            onPointerLeave={(e) => {
              // Hanya tetikus yang benar-benar "pergi". Jari yang diangkat
              // sesudah mengetuk juga mengirim `pointerleave` — di layar sentuh
              // itu berarti bilahnya muncul lalu hilang lagi seketika pada
              // setiap ketukan.
              if (e.pointerType !== 'mouse') return;
              titikRef.current = null;
              if (!berjalanRef.current || fokusBilahRef.current || menyeret.current) return;
              if (sembunyiRef.current) clearTimeout(sembunyiRef.current);
              setTampil(false);
            }}
            onFocusCapture={(e) => {
              fokusBilahRef.current = fokusPapanKetik(e.target as HTMLElement);
              bangunkanKendali();
            }}
            onBlurCapture={() => {
              fokusBilahRef.current = false;
              bangunkanKendali();
            }}
            /* `min-h`, bukan `min-h-0`: di layar pendek (ponsel mendatar,
               jendela PC yang dikecilkan) kotak yang boleh menyusut sampai nol
               akan benar-benar menyusut sampai nol demi memuat baris kendali di
               bawahnya, dan yang tersisa halaman tanpa gambar sama sekali.
               Dengan lantai ini halaman yang menggulung sedikit — kegagalan
               yang jauh lebih murah. */
            /* `touch-none` supaya cubitan dan seretan sampai ke sini alih-alih
               menggulung halaman; `select-none` supaya seretan tidak menyorot
               teks bilah di bawahnya. */
            style={tinggiVideo !== null ? { height: tinggiVideo } : undefined}
            className={`group relative min-h-[10rem] w-full ${
              tinggiVideo !== null ? 'flex-none' : 'flex-1'
            } touch-none select-none overflow-hidden rounded-lg bg-black ${
              !kendaliTampil && (main || mundur > 0) ? 'cursor-none' : ''
            } ${zoomGambar.s > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}
          >
            {/* Satu pembungkus untuk kedua elemen video: keduanya bertukar peran
                di batas segmen (§D8.1) dan wajib diperbesar dengan angka yang
                sama persis — kalau tidak, pergantian berkas terlihat seperti
                gambar yang meloncat. */}
            <div className="absolute inset-0" style={gayaZoom}>
            <video
              ref={vidA}
              onWaiting={aktifA ? onWaiting : undefined}
              onPlaying={aktifA ? onLancar : undefined}
              onCanPlay={aktifA ? onLancar : undefined}
              onTimeUpdate={aktifA ? onTimeUpdate : undefined}
              onEnded={aktifA ? onEnded : undefined}
              onError={aktifA ? onError : undefined}
              onLoadedMetadata={
                aktifA
                  ? (e) =>
                      setUkuranVideo({
                        w: e.currentTarget.videoWidth,
                        h: e.currentTarget.videoHeight,
                      })
                  : undefined
              }
              playsInline
              preload="auto"
              className={`absolute inset-0 h-full w-full object-contain ${aktifA ? '' : 'invisible'}`}
            />
            <video
              ref={vidB}
              onWaiting={aktifA ? undefined : onWaiting}
              onPlaying={aktifA ? undefined : onLancar}
              onCanPlay={aktifA ? undefined : onLancar}
              onTimeUpdate={aktifA ? undefined : onTimeUpdate}
              onEnded={aktifA ? undefined : onEnded}
              onError={aktifA ? undefined : onError}
              onLoadedMetadata={
                aktifA
                  ? undefined
                  : (e) =>
                      setUkuranVideo({
                        w: e.currentTarget.videoWidth,
                        h: e.currentTarget.videoHeight,
                      })
              }
              playsInline
              preload="auto"
              className={`absolute inset-0 h-full w-full object-contain ${aktifA ? 'invisible' : ''}`}
            />
            </div>

            {tersendat && main && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                <span className="h-10 w-10 animate-spin rounded-full border-2 border-white/30 border-t-white motion-reduce:animate-none" />
              </div>
            )}

            {/* Panel ukur jeda batas berkas. Duduk di dalam kotak gambar supaya
                ikut terbawa ke layar penuh — 32× justru paling sering dipakai di
                sana. `pointer-events-none` supaya ia tidak pernah merebut ketukan
                dari gambar di bawahnya. */}
            {diagAktif && (
              <div className="pointer-events-none absolute right-2 top-2 z-10 rounded-md bg-black/70 p-2 font-mono text-[11px] leading-snug text-white ring-1 ring-white/20">
                <div className="mb-1 font-semibold">
                  jeda batas berkas · pendengar {pendengarHidup.current}
                </div>
                {diagBaris.length === 0 ? (
                  <div className="text-white/60">belum ada pergantian berkas</div>
                ) : (
                  diagBaris.map((d, i) => (
                    <div
                      key={diagBaris.length - i}
                      className={d.jedaMs > 1000 ? 'text-red-300' : 'text-white/90'}
                    >
                      {Math.round(d.jedaMs)} ms · {d.laju}× · pra-muat{' '}
                      {d.praMuat ? 'ya' : 'TIDAK'}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Angka zoom muncul hanya kalau memang sedang diperbesar — ia
                jawaban atas "kenapa gambarnya beda dari tadi", sekaligus jalan
                pulang yang tidak menuntut orang menemukan ketuk ganda sendiri. */}
            {zoomGambar.s > 1 && (
              <button
                type="button"
                data-kendali=""
                onClick={resetZoom}
                title={t('Back to 1× (double-tap the picture works too)')}
                className="absolute left-2 top-2 z-10 flex min-h-tap items-center gap-1.5 rounded-md bg-black/50 px-2.5 text-sm font-semibold tabular-nums text-white ring-1 ring-white/20 transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {zoomGambar.s.toFixed(1)}×
                <span className="text-xs font-normal text-white/70">{t('reset')}</span>
              </button>
            )}

            {/* Daftar klip duduk DI DALAM kotak gambar, bukan di bawah halaman:
                dengan begitu ia ikut masuk ke layar penuh — dan layar penuh
                justru tempat orang paling lama menonton. */}
            {panel === 'klip' && (
              <PanelKlip
                segments={segments}
                aktifId={segAktif === null ? null : (segments[segAktif]?.id ?? null)}
                onPilih={(ms) => seek(ms)}
                onTutup={() => setPanel(null)}
              />
            )}

            {panel === 'bookmark' && (
              <PanelBookmark
                bookmarks={bookmarks}
                onPilih={(ms) => seek(ms)}
                onUbah={muatBookmark}
                onTutup={() => setPanel(null)}
              />
            )}

            {/* Kotak label bookmark (§G10.7): terbuka DI ATAS playhead, di
                dalam gambar, dan video tidak berhenti selama ia terbuka. */}
            {tandaBaru && (
              <div
                data-kendali=""
                className="absolute inset-x-0 bottom-24 z-30 mx-auto w-[min(28rem,90%)] rounded-md bg-black/85 p-3 text-white backdrop-blur-sm"
              >
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const label = labelBaru.trim();
                    if (label === '' || !cameraId) return;
                    void (async () => {
                      try {
                        await createBookmark(cameraId, tandaBaru.atMs, label);
                        setTandaBaru(null);
                        setLabelBaru('');
                        await muatBookmark();
                      } catch (err) {
                        setBookmarkError((err as Error).message);
                      }
                    })();
                  }}
                >
                  <label className="block text-xs text-white/70">
                    {t('Bookmark at {jam}', { jam: formatWibJam(tandaBaru.atMs) })}
                  </label>
                  <input
                    autoFocus
                    value={labelBaru}
                    onChange={(e) => setLabelBaru(e.target.value)}
                    onKeyDown={(e) => {
                      // Esc membatalkan. `stopPropagation` supaya ia tidak ikut
                      // membatalkan mark in/out di peta tombol halaman.
                      if (e.key === 'Escape') {
                        e.stopPropagation();
                        setTandaBaru(null);
                      }
                    }}
                    maxLength={160}
                    placeholder={t('e.g. White car enters')}
                    className="mt-1 min-h-tap w-full rounded-md border border-white/25 bg-black/40 px-3 text-sm text-white placeholder:text-white/40"
                  />
                  {bookmarkError && (
                    <p role="alert" className="mt-1 text-xs text-amber-400">
                      {bookmarkError}
                    </p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <button
                      type="submit"
                      disabled={labelBaru.trim() === ''}
                      className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast disabled:opacity-50"
                    >
                      {t('Save bookmark')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTandaBaru(null)}
                      className="min-h-tap rounded-md border border-white/25 px-4 text-sm"
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Kepala gambar: siapa, dan sedang jam berapa.

                Nama kamera ditulis DI DALAM gambar, bukan cuma di bilah filter
                yang bisa dilipat — kalau tidak, layar penuh (dan tangkapan
                layar yang diambil dari situ) tidak menyebutkan kamera mana yang
                sedang dilihat, dan itu justru satu-satunya keterangan yang
                harus ikut ke dalam laporan. */}
            <div className="pointer-events-none absolute left-3 top-2 z-10 max-w-[60%] drop-shadow">
              <p className="truncate text-sm font-semibold text-white sm:text-base">
                {kamera?.name ?? ''}
              </p>
              <p className="truncate text-[11px] text-white/70 tabular-nums">
                {ukuranVideo && `${ukuranVideo.w} × ${ukuranVideo.h}`}
                {ukuranVideo && kamera && ' · '}
                {kamera && t('{n} fps', { n: kamera.streamFps })}
              </p>
            </div>

            {/* Jam dinding + tombol gambar, satu kelompok di kanan atas.

                Jamnya sengaja di sini dan bukan cuma di bawah timeline: yang
                sedang dibaca orang gambarnya, dan jam yang harus dicari dengan
                menurunkan pandangan bukan jam yang dipercaya saat menyebut
                "kejadiannya pukul sekian". Ia juga satu-satunya keterangan waktu
                yang ikut terbawa ke layar penuh. */}
            <div className="absolute right-2 top-2 z-10 flex items-center gap-2">
              {posMs !== null && (
                <span
                  data-kendali=""
                  className="rounded-md bg-black/70 px-2.5 py-1.5 text-sm font-medium tabular-nums text-white ring-1 ring-white/20"
                >
                  {formatWibTanggal(posMs)} {formatWibJam(posMs)}
                </span>
              )}

              <button
                type="button"
                data-kendali=""
                onClick={simpanFrame}
                disabled={posMs === null}
                title={t('Save this frame (PNG)')}
                aria-label={t('Save this frame as a picture')}
                className="flex h-11 w-11 items-center justify-center rounded-md bg-black/50 text-white ring-1 ring-white/20 transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-40"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14.5 4h-5L8 6H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-4z" />
                  <circle cx="12" cy="13" r="3.5" />
                </svg>
              </button>

              {/* Pola yang sama dengan ikon tile (§G7 no.5): selalu terlihat,
                  44×44, menguat saat hover. Perangkat sentuh tidak punya hover,
                  jadi ikon yang cuma muncul saat hover sama dengan tidak ada. */}
              <button
                type="button"
                onClick={layarPenuh}
                data-kendali=""
                title={penuh ? t('Exit fullscreen (F)') : t('Fullscreen (F)')}
                aria-label={penuh ? t('Leave fullscreen') : t('Play the video fullscreen')}
                className="flex h-11 w-11 items-center justify-center rounded-md bg-black/50 text-white ring-1 ring-white/20 transition-colors hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {/* Ikonnya berbalik di dalam layar penuh. */}
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {penuh ? (
                    <>
                      <path d="M3 8h3a2 2 0 0 0 2-2V3" />
                      <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                      <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                      <path d="M21 16h-3a2 2 0 0 0-2 2v3" />
                    </>
                  ) : (
                    <>
                      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                    </>
                  )}
                </svg>
              </button>
            </div>

            {/* Panah pindah-potongan yang dulu melayang di kiri-kanan gambar
                dibuang 8 September 2026, atas permintaan lapangan: *"untuk
                'clip berikutnya' gak usah ada tombol < > di layar, di tombol
                control aja udah cukup."*

                Keduanya pindah ke tempat kendali memang berkumpul — kelompok
                transport di bawah gambar DAN bilah pemutar di dalamnya
                (`PlayerBar`), yang kedua penting karena di layar penuh dan di
                ponsel cuma bilah itu yang bisa dijangkau (§G10.3). Papan ketik
                mendapat `PgUp`/`PgDn` (§G10.1).

                Yang hilang bersamanya bukan cuma dua tombol: keduanya menutupi
                bagian kiri dan kanan gambar persis di ketinggian mata orang
                yang sedang memeriksa rekaman, dan di layar penuh itu justru
                bagian yang paling sering ada isinya. */}

            {/* Bilah pemutar di dalam gambar (§G10.3) — satu-satunya kendali
                yang ikut masuk ke layar penuh. */}
            {bilahDalamGambar && fromMs !== null && toMs !== null && toMs > fromMs && (
              <PlayerBar
                fromMs={fromMs}
                toMs={toMs}
                positionMs={posMs}
                segments={segments}
                main={main}
                mundur={mundur}
                rate={rate}
                tampil={kendaliTampil}
                besar={penuh}
                onSeek={seek}
                onPutar={putar}
                onJeda={jeda}
                onMundur={mundurLebihCepat}
                onMaju={majuLebihCepat}
                onNormal={lajuNormal}
                onTahan={(v) => {
                  hoverBilahRef.current = v;
                  bangunkanKendali();
                }}
                onSeret={tandaiSeret}
                onKlipSebelum={() => lompatSegmen(-1)}
                onKlipSesudah={() => lompatSegmen(1)}
              />
            )}
          </div>

          {aturUkuran && (
            <GarisPembatas
              label={t('Drag to resize the picture (double-click to reset)')}
              nilai={() => wadahRef.current?.getBoundingClientRect().height ?? 0}
              min={160}
              // Lebih tinggi dari selebar-kotak cuma menambah pita hitam atas-bawah.
              max={() => {
                const el = wadahRef.current;
                if (!el) return 160;
                const rasio = ukuranVideo ? ukuranVideo.h / ukuranVideo.w : 9 / 16;
                return Math.max(el.clientWidth * rasio, el.clientHeight);
              }}
              onUbah={(px) => setUkuranPanel((u) => ({ ...u, video: px }))}
              onReset={() => setUkuranPanel((u) => ({ ...u, video: null }))}
            />
          )}

          {/* Timeline dikunci tepat di bawah gambar. */}
          {view && fromMs !== null && toMs !== null && (
            <div className="shrink-0">
              <PlaybackTimeline
                minMs={batasSumbu.min}
                maxMs={batasSumbu.max}
                fromMs={fromMs}
                toMs={toMs}
                viewFromMs={view.from}
                viewToMs={view.to}
                onViewChange={(from, to) => setView({ from, to })}
                onSeret={tandaiSeret}
                segments={segments}
                positionMs={posMs}
                onSeek={seek}
                markInMs={markIn}
                markOutMs={markOut}
                bookmarks={bookmarks.map((b) => ({
                  id: b.id,
                  atMs: new Date(b.at).getTime(),
                  label: b.label,
                  expired: b.expired,
                }))}
                onPilihBookmark={(ms) => seek(ms)}
                tinggiPita={tinggiPita}
              />
              {aturUkuran && (
                <GarisPembatas
                  label={t('Drag to resize the timeline (double-click to reset)')}
                  nilai={() => ukuranPanel.pita}
                  min={TINGGI_PITA}
                  max={() => 120}
                  onUbah={(px) => setUkuranPanel((u) => ({ ...u, pita: px }))}
                  onReset={() => setUkuranPanel((u) => ({ ...u, pita: TINGGI_PITA }))}
                />
              )}

              {/* Keterangan warna + kendali sumbu.

                  Legendanya bukan hiasan: biru dan abu-abu di pita segmen
                  menjawab pertanyaan yang paling sering salah dijawab di
                  halaman ini — "kok gelap, kameranya mati?" vs "memang tidak
                  ada rekamannya". §G3.1 melarang menyampaikan keadaan hanya
                  lewat warna, jadi warnanya diberi nama di sini. */}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-accent" aria-hidden="true" />
                  {t('Recording')}
                </span>
                <span className="flex items-center gap-1.5 text-muted">
                  <span className="h-2.5 w-2.5 rounded-full bg-line" aria-hidden="true" />
                  {t('No recording')}
                </span>

                <div className="ml-auto flex flex-wrap items-center justify-end gap-1">
                  <KendaliSkala
                    rentangMs={view.to - view.from}
                    penuhMs={batasSumbu.max - batasSumbu.min}
                    onRentang={(ms) => zoomSumbu(ms / Math.max(1, view.to - view.from))}
                    onMuatPenuh={() => setView({ from: fromMs, to: toMs })}
                  />

                  {/* Lompat ke jam yang sudah diketahui. Investigasi hampir
                      selalu dimulai dari sebuah jam yang disebut orang, bukan
                      dari menggeser timeline sampai ketemu. */}
                  {keJam === null ? (
                    <button
                      type="button"
                      onClick={() =>
                        setKeJam(
                          posMs === null
                            ? '00:00'
                            : toLocalInput(new Date(posMs)).slice(11, 16),
                        )
                      }
                      className="flex min-h-tap items-center gap-1.5 rounded-md border border-line px-3 hover:bg-subtle"
                    >
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 2" />
                      </svg>
                      {t('Go to time')}
                    </button>
                  ) : (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={(e) => {
                        e.preventDefault();
                        const [j, m] = keJam.split(':').map(Number);
                        if (j === undefined || m === undefined || Number.isNaN(j)) return;
                        // Tanggalnya diambil dari jendela yang sedang dilihat,
                        // bukan dari hari ini: orang yang sedang memeriksa
                        // kemarin menyebut jam, bukan tanggal.
                        const d = new Date(view.from);
                        d.setHours(j, m, 0, 0);
                        let ms = d.getTime();
                        // Rentang yang melewati tengah malam: jam kecil berarti
                        // hari berikutnya, bukan lompat mundur ke luar jendela.
                        if (ms < view.from && ms + 86_400_000 <= view.to) ms += 86_400_000;
                        seek(ms);
                        setKeJam(null);
                      }}
                    >
                      <input
                        autoFocus
                        type="time"
                        value={keJam}
                        onChange={(e) => setKeJam(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setKeJam(null);
                          }
                        }}
                        aria-label={t('Go to time')}
                        className="min-h-tap rounded-md border border-line bg-surface px-2 text-xs tabular-nums"
                      />
                      <button
                        type="submit"
                        className="min-h-tap rounded-md bg-accent px-3 text-xs text-accent-contrast"
                      >
                        {t('Go')}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          )}


          {/* Satu baris kendali, dan hanya di layar yang tidak memakai bilah
              dalam gambar — lihat `bilahDalamGambar`. Tata letaknya: laju di
              kiri, transport di tengah, perbuatan di kanan. */}
          {!bilahDalamGambar && (
            <div className="flex shrink-0 items-center gap-2">
              {/* Laju sebagai daftar, bukan cuma tombol yang menaikkan satu
                  tingkat: yang tahu persis mau 8× tidak perlu menekan ⏩ tiga
                  kali dan menghitung. Tombol ⏩/⏪ tetap ada di tengah untuk
                  yang memang sedang menyusuri. */}
              <label className="flex items-center gap-1.5 text-sm">
                <span className="sr-only">{t('Playback speed')}</span>
                <select
                  value={mundur > 0 ? `-${mundur}` : String(rate)}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v < 0) {
                      setRate(1);
                      setMain(false);
                      setMundur(-v);
                    } else {
                      setMundur(0);
                      setRate(v);
                      putar();
                    }
                  }}
                  className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm font-semibold tabular-nums"
                >
                  {[...SHUTTLE].reverse().map((v) => (
                    <option key={`-${v}`} value={`-${v}`}>
                      −{v}×
                    </option>
                  ))}
                  {SHUTTLE.map((v) => (
                    <option key={v} value={v}>
                      {v}×
                    </option>
                  ))}
                </select>
              </label>

              <div
                className="mx-auto flex items-center gap-1 rounded-full border border-line bg-surface p-1 shadow-sm"
                role="group"
                aria-label={t('Playback controls')}
              >
                <TombolTransport
                  onClick={() => lompatSegmen(-1)}
                  title={t('Previous clip (PgUp)')}
                  label={t('Previous clip')}
                  aktif={false}
                  tanda={null}
                >
                  <path d="M20 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 20 14.5z" />
                  <rect x="4" y="4.6" width="2.6" height="10.8" rx="1" />
                </TombolTransport>

                <TombolTransport
                  onClick={mundurLebihCepat}
                  title={t('Rewind (J)')}
                  label={mundur > 0 ? t('Rewinding {n}×', { n: mundur }) : t('Rewind')}
                  aktif={mundur > 0}
                  tanda={mundur > 1 ? `${mundur}×` : null}
                >
                  {/* ⏪ — dua segitiga menghadap kiri. */}
                  <path d="M11 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 11 14.5z" />
                  <path d="M21 5.5a.8.8 0 0 0-1.24-.66l-7 4.5a.8.8 0 0 0 0 1.32l7 4.5A.8.8 0 0 0 21 14.5z" />
                </TombolTransport>

                <button
                  type="button"
                  onClick={() => (main || mundur ? jeda() : putar())}
                  title={main || mundur ? t('Pause (Space)') : t('Play (Space)')}
                  aria-label={main || mundur ? t('Pause') : t('Play')}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-contrast transition-[background-color,scale] duration-150 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="currentColor" aria-hidden="true">
                    {main || mundur ? (
                      <>
                        <rect x="6.5" y="5" width="3.6" height="14" rx="1" />
                        <rect x="13.9" y="5" width="3.6" height="14" rx="1" />
                      </>
                    ) : (
                      // Sedikit digeser ke kanan: segitiga yang dipusatkan
                      // secara geometris selalu terlihat terlalu ke kiri di
                      // dalam lingkaran — pusat massanya bukan pusat kotaknya.
                      <path d="M9 5.14a1 1 0 0 1 1.53-.85l9.72 6.86a1 1 0 0 1 0 1.7l-9.72 6.86A1 1 0 0 1 9 18.86z" />
                    )}
                  </svg>
                </button>

                <TombolTransport
                  onClick={majuLebihCepat}
                  title={t('Fast forward (L)')}
                  label={rate > 1 ? t('Forward {n}×', { n: rate }) : t('Fast forward')}
                  aktif={rate > 1}
                  tanda={rate > 1 ? `${rate}×` : null}
                >
                  {/* ⏩ — dua segitiga menghadap kanan. */}
                  <path d="M3 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 3 14.5z" />
                  <path d="M13 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 13 14.5z" />
                </TombolTransport>

                <TombolTransport
                  onClick={() => lompatSegmen(1)}
                  title={t('Next clip (PgDn)')}
                  label={t('Next clip')}
                  aktif={false}
                  tanda={null}
                >
                  <path d="M4 5.5a.8.8 0 0 1 1.24-.66l7 4.5a.8.8 0 0 1 0 1.32l-7 4.5A.8.8 0 0 1 4 14.5z" />
                  <rect x="17.4" y="4.6" width="2.6" height="10.8" rx="1" />
                </TombolTransport>
              </div>

              {/* Perbuatan. Yang tinggal di layar cuma yang dipakai tiap kali. */}
              <TombolIkon
                nama="markIn"
                label={t('Mark in (I)')}
                onClick={() => posMs !== null && setMarkIn(posMs)}
                disabled={posMs === null}
              />
              <TombolIkon
                nama="markOut"
                label={t('Mark out (O)')}
                onClick={() => posMs !== null && setMarkOut(posMs)}
                disabled={posMs === null}
              />
              <TombolIkon
                nama="download"
                utama
                label={
                  unduhan?.status === 'queued' || unduhan?.status === 'running'
                    ? t('Preparing file…')
                    : t('Download {durasi}', { durasi: formatDuration(durasiUnduhS) })
                }
                onClick={() => void mintaUnduhan()}
                disabled={
                  !rentangUnduh ||
                  rentangTerlaluPanjang ||
                  unduhan?.status === 'queued' ||
                  unduhan?.status === 'running'
                }
              />
              <TombolIkon
                nama="bookmark"
                label={t('Bookmarks ({n})', { n: bookmarks.length })}
                tanda={bookmarks.length}
                aktif={panel === 'bookmark'}
                aria-pressed={panel === 'bookmark'}
                onClick={() => setPanel((v) => (v === 'bookmark' ? null : 'bookmark'))}
              />

              <div className="relative">
                <TombolIkon
                  nama="more"
                  label={t('More')}
                  aktif={lainnya}
                  aria-expanded={lainnya}
                  onClick={() => setLainnya((v) => !v)}
                />
                {lainnya && (
                  <PanelLainnya
                    segments={segments}
                    panelAktif={panel}
                    onPanel={(v) => setPanel(v)}
                    markIn={markIn}
                    markOut={markOut}
                    onHapusTanda={() => {
                      setMarkIn(null);
                      setMarkOut(null);
                    }}
                    namaExport={namaExport}
                    onNamaExport={setNamaExport}
                    gaps={timeline?.gaps.length ?? 0}
                    bantuan={bantuan}
                    onBantuan={() => setBantuan((v) => !v)}
                    onTutup={() => setLainnya(false)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Di ponsel dan di layar penuh kendalinya ada di dalam gambar, jadi
              yang tersisa di sini cuma yang tidak muat di sana: tanda, unduh,
              dan pintu ke "Lainnya". */}
          {bilahDalamGambar && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <TombolIkon
                nama="markIn"
                label={t('Mark in (I)')}
                onClick={() => posMs !== null && setMarkIn(posMs)}
                disabled={posMs === null}
              />
              <TombolIkon
                nama="markOut"
                label={t('Mark out (O)')}
                onClick={() => posMs !== null && setMarkOut(posMs)}
                disabled={posMs === null}
              />
              <TombolIkon
                nama="download"
                utama
                label={
                  unduhan?.status === 'queued' || unduhan?.status === 'running'
                    ? t('Preparing file…')
                    : t('Download {durasi}', { durasi: formatDuration(durasiUnduhS) })
                }
                onClick={() => void mintaUnduhan()}
                disabled={
                  !rentangUnduh ||
                  rentangTerlaluPanjang ||
                  unduhan?.status === 'queued' ||
                  unduhan?.status === 'running'
                }
              />
              <TombolIkon
                nama="more"
                label={t('More')}
                aktif={lainnya}
                aria-expanded={lainnya}
                onClick={() => setLainnya((v) => !v)}
              />
            </div>
          )}

          {/* Strip pratinjau per menit (P4), DI BAWAH transport.

              Urutannya mengikuti mockup dan bukan selera: timeline menjawab
              "di mana saya", transport "apa yang saya lakukan", strip "apa yang
              akan saya lihat kalau pindah ke sana". Strip yang menyelinap di
              antara timeline dan transport memisahkan playhead dari tombol yang
              menggerakkannya — dua benda yang justru dipakai bergantian dalam
              satu tarikan tangan. */}
          {view && (
            <StripSprite
              segments={segments}
              positionMs={posMs}
              viewFromMs={view.from}
              viewToMs={view.to}
              batasMinMs={batasSumbu.min}
              batasMaksMs={batasSumbu.max}
              onSeek={seek}
              onGeserJendela={geserJendela}
            />
          )}

          {/* Isi pratayang yang bolong untuk kamera ini (§D8.1).
              Ditaruh tepat di bawah strip — di situlah bolongnya terlihat, dan
              tombol perbaikan yang jauh dari masalahnya tidak akan ditemukan
              orang yang sedang melihat masalahnya.

              Staf saja: API-nya menolak yang lain (§F8), dan tombol yang selalu
              menjawab "akses ditolak" cuma mengajari orang bahwa ada sesuatu
              yang tidak boleh mereka sentuh (§G9). */}
          {staf && cameraId && (
            <div className="shrink-0" data-kendali="">
              <TombolIsiPratayang cameraId={cameraId} label={t('Fill blank thumbnails')} />
            </div>
          )}

          {lainnya && bilahDalamGambar && (
            <PanelLainnya
              lembar
              segments={segments}
              panelAktif={panel}
              onPanel={(v) => setPanel(v)}
              markIn={markIn}
              markOut={markOut}
              onHapusTanda={() => {
                setMarkIn(null);
                setMarkOut(null);
              }}
              namaExport={namaExport}
              onNamaExport={setNamaExport}
              gaps={timeline?.gaps.length ?? 0}
              bantuan={bantuan}
              onBantuan={() => setBantuan((v) => !v)}
              onTutup={() => setLainnya(false)}
            />
          )}

          {bantuan && <PetaTombol />}

          {/* Bilah antrean export (§G10.8) — muncul hanya saat ada yang sedang
              dikerjakan. Meminta export tidak memindahkan orang ke halaman lain:
              bilahnya muncul di tempat, dan pemutaran tidak terganggu. */}
          {unduhan && (unduhan.status === 'queued' || unduhan.status === 'running') && (
            <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line px-3 py-2 text-sm">
              <span className="font-medium">
                {unduhan.name ?? t('Untitled export')}
              </span>
              {unduhan.status === 'queued' ? (
                <span className="text-muted">
                  {unduhan.queuePosition
                    ? t('Waiting — number {n} in the queue', { n: unduhan.queuePosition })
                    : t('Waiting its turn')}
                </span>
              ) : (
                <>
                  <span className="h-2 w-40 overflow-hidden rounded bg-subtle">
                    <span
                      className="block h-full bg-accent transition-[width] duration-500"
                      style={{ width: `${unduhan.progressPct ?? 0}%` }}
                    />
                  </span>
                  <span className="tabular-nums text-muted">
                    {unduhan.progressPct === null || unduhan.progressPct === undefined
                      ? t('starting…')
                      : `${unduhan.progressPct}%`}
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  void dropExport(unduhan.id)
                    .then(() => setUnduhan(null))
                    .catch((e) => setUnduhError((e as Error).message));
                }}
                className="min-h-tap rounded-md border border-line px-3 text-xs"
              >
                {t('Cancel')}
              </button>
              <Link to="/downloads" className="text-xs text-accent-text underline underline-offset-2">
                {t('Download Center')}
              </Link>
            </div>
          )}

          {/* Keterangan unduhan: satu baris, bukan panel (§G10 poin 4, §D8.2). */}
          <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="text-muted">
              {markIn !== null && markOut !== null && markOut > markIn
                ? t('Download from marks: {dari} – {sampai}', {
                    dari: formatWibJam(markIn),
                    sampai: formatWibJam(markOut),
                  })
                : t('Whole range')}
              {bolongTerpilih > 0 && (
                <>
                  {' · '}
                  {t('{durasi} of gaps will be skipped', {
                    durasi: formatDuration(Math.round(bolongTerpilih / 1000)),
                  })}
                </>
              )}
            </span>

            {rentangTerlaluPanjang && (
              <span className="text-amber-600" role="alert">
                {t('Downloads are capped at {jam} hours — narrow the range or use mark in/out.', {
                  jam: RENTANG_UNDUH_MAX_S / 3600,
                })}
              </span>
            )}

            {unduhError && (
              <span className="text-amber-600" role="alert">
                {unduhError}
              </span>
            )}

            {unduhan?.status === 'failed' && (
              <span className="text-amber-600" role="alert">
                {unduhan.error ?? t('The file could not be prepared.')}
              </span>
            )}

            {unduhan?.status === 'ready' && (
              <>
                {/* Unduhannya sudah jalan sendiri. Tautan ini jaring pengaman:
                    browser boleh memblokir unduhan yang tidak berasal dari klik
                    langsung. */}
                <a
                  href={`/api/v1/exports/${unduhan.id}/download`}
                  download
                  className="flex min-h-tap items-center rounded-md border border-line px-3 hover:bg-subtle"
                >
                  {t('Download again')}
                </a>
                <span className="text-muted">
                  {unduhan.actualFrom && unduhan.actualTo && (
                    <>
                      {formatWibJam(new Date(unduhan.actualFrom).getTime())} –{' '}
                      {formatWibJam(new Date(unduhan.actualTo).getTime())}
                    </>
                  )}
                  {unduhan.sizeBytes !== null && <> · {formatBytes(unduhan.sizeBytes)}</>}
                  {' · '}
                  {t('deleted 30 minutes after download')}
                </span>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Panel "Lainnya" — popover di desktop, lembar bawah di ponsel (§G10 poin 4).
 *
 * Isinya **tidak ada yang baru**. Semua yang di sini sudah ada sebelum revisi
 * ini dan perilakunya tidak berubah sedikit pun; yang berubah cuma ia tidak
 * lagi menempati layar sepanjang waktu. Itu juga sebabnya panel ini tidak boleh
 * tumbuh: begitu ada yang menaruh kendali yang dipakai tiap menit di sini,
 * barisnya akan diminta kembali ke luar, dan kita kembali ke sebelas tombol.
 *
 * Dua wujud, satu isi. Di ponsel ia lembar yang naik dari bawah — jempol
 * sampai di sana tanpa memindahkan genggaman; di desktop ia popover yang
 * menempel pada tombolnya, karena di sana yang menekan adalah penunjuk dan
 * jarak tempuhnya harus pendek.
 */
function PanelLainnya({
  lembar = false,
  segments,
  panelAktif,
  onPanel,
  markIn,
  markOut,
  onHapusTanda,
  namaExport,
  onNamaExport,
  gaps,
  bantuan,
  onBantuan,
  onTutup,
}: {
  /** `true` = lembar bawah (ponsel), `false` = popover (desktop). */
  lembar?: boolean;
  segments: TimelineSegment[];
  panelAktif: 'klip' | 'bookmark' | null;
  onPanel: (v: 'klip' | 'bookmark' | null) => void;
  markIn: number | null;
  markOut: number | null;
  onHapusTanda: () => void;
  namaExport: string;
  onNamaExport: (v: string) => void;
  gaps: number;
  bantuan: boolean;
  onBantuan: () => void;
  onTutup: () => void;
}) {
  const { t } = useBahasa();
  const kotakRef = useRef<HTMLDivElement>(null);

  /**
   * Esc menutup, dan `stopPropagation` supaya ia tidak ikut membatalkan
   * mark in/out di peta tombol halaman — kesalahan yang sama sudah pernah
   * dibayar di kotak label bookmark.
   */
  useEffect(() => {
    const onTombol = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onTutup();
      }
    };
    document.addEventListener('keydown', onTombol);
    return () => document.removeEventListener('keydown', onTombol);
  }, [onTutup]);

  useEffect(() => {
    const onLuar = (e: PointerEvent) => {
      if (!kotakRef.current?.contains(e.target as Node)) onTutup();
    };
    // Ditunda satu putaran: klik yang MEMBUKA panel ini masih dalam perjalanan,
    // dan tanpa penundaan ia langsung menutupnya kembali.
    const id = setTimeout(() => document.addEventListener('pointerdown', onLuar), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', onLuar);
    };
  }, [onTutup]);

  const isi = (
    <>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          aria-pressed={panelAktif === 'klip'}
          onClick={() => onPanel(panelAktif === 'klip' ? null : 'klip')}
          className={`min-h-tap rounded-md border px-3 text-sm ${
            panelAktif === 'klip' ? 'border-accent text-accent-text' : 'border-line hover:bg-subtle'
          }`}
        >
          {t('Clips ({n})', { n: segments.length })}
        </button>
        <button
          type="button"
          onClick={onHapusTanda}
          disabled={markIn === null && markOut === null}
          className="min-h-tap rounded-md border border-line px-3 text-sm hover:bg-subtle disabled:opacity-40"
        >
          {t('Clear marks')}
        </button>
        <button
          type="button"
          aria-pressed={bantuan}
          onClick={onBantuan}
          className={`min-h-tap rounded-md border px-3 text-sm ${
            bantuan ? 'border-accent text-accent-text' : 'border-line hover:bg-subtle'
          }`}
        >
          {t('Help (?)')}
        </button>
      </div>

      {/* Nama berkas diisi DI TEMPAT orang menekan tombolnya (§G10.8), bukan di
          layar lain. Kosong tetap sah — ia jatuh ke nama bawaan. */}
      <label className="mt-3 block text-sm">
        {t('Export file name')}
        <input
          value={namaExport}
          onChange={(e) => onNamaExport(e.target.value)}
          maxLength={160}
          placeholder={t('File name (optional)')}
          className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm"
        />
      </label>

      {gaps > 0 && (
        <p className="mt-3 text-xs text-muted">{t('{n} gaps', { n: gaps })}</p>
      )}
    </>
  );

  if (lembar) {
    return (
      <div
        ref={kotakRef}
        role="dialog"
        aria-label={t('More')}
        className="fixed inset-x-0 bottom-0 z-40 rounded-t-2xl border-t border-line bg-surface p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl"
      >
        {/* Pegangan lembar — penanda bahwa ini bisa ditutup, bukan hiasan. */}
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-line" aria-hidden="true" />
        {isi}
        <button
          type="button"
          onClick={onTutup}
          className="mt-4 min-h-tap w-full rounded-md border border-line text-sm"
        >
          {t('Close')}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={kotakRef}
      role="dialog"
      aria-label={t('More')}
      className="absolute bottom-full right-0 z-40 mb-2 w-72 rounded-lg border border-line bg-surface p-3 shadow-xl"
    >
      {isi}
    </div>
  );
}

/**
 * Daftar bookmark di dalam kotak gambar (§G10.7).
 *
 * Dua wujud untuk satu data, dan keduanya wajib ada: penanda di timeline untuk
 * **menandai**, daftar ini untuk **kembali**. Diklik → playhead lompat, video
 * ikut seketika.
 */
function PanelBookmark({
  bookmarks,
  onPilih,
  onUbah,
  onTutup,
}: {
  bookmarks: Bookmark[];
  onPilih: (ms: number) => void;
  onUbah: () => Promise<void> | void;
  onTutup: () => void;
}) {
  const { t } = useBahasa();
  const [sunting, setSunting] = useState<{ id: string; label: string } | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [akanDihapus, setAkanDihapus] = useState<Bookmark | null>(null);

  return (
    <div
      data-kendali=""
      className="absolute inset-y-0 right-0 z-20 flex w-80 max-w-[85%] flex-col bg-black/80 text-white backdrop-blur-sm"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/15 px-3 py-2">
        <span className="text-sm font-medium">
          {t('Bookmarks ({n})', { n: bookmarks.length })}
        </span>
        <button
          type="button"
          onClick={onTutup}
          aria-label={t('Close the bookmark list')}
          className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          ×
        </button>
      </div>

      {bookmarks.length === 0 ? (
        <p className="p-3 text-xs text-white/60">
          {t('No bookmarks in this range. Press M while watching to add one.')}
        </p>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {bookmarks.map((b) => {
            const ms = new Date(b.at).getTime();
            return (
              <li key={b.id} className="border-b border-white/10">
                {sunting?.id === b.id ? (
                  <form
                    className="p-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const label = sunting.label.trim();
                      if (label === '') return;
                      void (async () => {
                        setSibuk(true);
                        try {
                          await renameBookmark(b.id, label);
                          setSunting(null);
                          await onUbah();
                        } finally {
                          setSibuk(false);
                        }
                      })();
                    }}
                  >
                    <input
                      autoFocus
                      value={sunting.label}
                      maxLength={160}
                      onChange={(e) => setSunting({ id: b.id, label: e.target.value })}
                      className="min-h-tap w-full rounded border border-white/25 bg-black/40 px-2 text-sm text-white"
                    />
                    <div className="mt-1 flex gap-2">
                      <button type="submit" disabled={sibuk} className="text-xs underline">
                        {t('Save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSunting(null)}
                        className="text-xs underline"
                      >
                        {t('Cancel')}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-start gap-1 p-2">
                    {/* Baris yang rekamannya sudah lewat retensi diredupkan dan
                        tidak bisa diklik (§D8.7): ada catatannya, isinya sudah
                        tidak ada — kalimat yang sama persis dengan berkas export
                        yang sudah disapu (§D8.9). */}
                    <button
                      type="button"
                      disabled={b.expired}
                      onClick={() => onPilih(ms)}
                      className={`min-w-0 flex-1 text-left ${b.expired ? 'opacity-50' : 'hover:underline'}`}
                    >
                      <span className="block font-mono text-xs text-white/70">
                        {formatWibTanggal(ms)} {formatWibJam(ms)}
                      </span>
                      <span className="block truncate text-sm">{b.label}</span>
                      {b.expired && (
                        <span className="block text-[11px] text-white/50">
                          {t('recording past its retention')}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSunting({ id: b.id, label: b.label })}
                      aria-label={t('Rename bookmark')}
                      className="rounded p-1 text-xs text-white/60 hover:bg-white/20"
                    >
                      ✎
                    </button>
                    {/* `confirm()` bawaan browser sudah tidak dipakai di sini
                        (§G17). Yang hilang memang cuma satu baris teks — tapi
                        titik waktu di dalamnya adalah seluruh isinya, dan
                        menemukannya lagi berarti menyisir ulang rekaman
                        berjam-jam yang tadi sudah selesai disisir. */}
                    <button
                      type="button"
                      disabled={sibuk}
                      onClick={() => setAkanDihapus(b)}
                      aria-label={t('Delete bookmark')}
                      className="rounded p-1 text-xs text-white/60 hover:bg-white/20"
                    >
                      ×
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {akanDihapus && (
        <DialogHapus
          nama={akanDihapus.label}
          judul={t('Delete bookmark')}
          keterangan={t(
            'The recording itself is not touched — only this marker and the time it points at.',
          )}
          onBatal={() => setAkanDihapus(null)}
          onHapus={async (k) => {
            await deleteBookmark(akanDihapus.id, k);
            setAkanDihapus(null);
            await onUbah();
          }}
        />
      )}
    </div>
  );
}

/**
 * Daftar klip di dalam kotak gambar (§G10.5).
 *
 * Yang ditawarkan di sini berbeda dengan timeline, dan bukan pengulangannya:
 * timeline menjawab *"jam berapa"*, daftar ini menjawab *"berkas yang mana"*.
 * Keduanya dibutuhkan begitu rekamannya harus keluar dari Lensa — diserahkan ke
 * orang lain, dilampirkan ke laporan — karena yang diserahkan selalu satu
 * berkas utuh, bukan potongan jam.
 */
function PanelKlip({
  segments,
  aktifId,
  onPilih,
  onTutup,
}: {
  segments: Timeline['segments'];
  aktifId: string | null;
  onPilih: (ms: number) => void;
  onTutup: () => void;
}) {
  const { t } = useBahasa();
  const MAKS = 500;
  return (
    <div
      data-kendali=""
      className="absolute inset-y-0 right-0 z-20 flex w-80 max-w-[85%] flex-col bg-black/80 text-white backdrop-blur-sm"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/15 px-3 py-2">
        <span className="text-sm font-medium">{t('Clips ({n})', { n: segments.length })}</span>
        <button
          type="button"
          onClick={onTutup}
          aria-label={t('Close the clip list')}
          className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6 18 18M18 6 6 18" />
          </svg>
        </button>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {segments.slice(0, MAKS).map((s) => {
          const a = new Date(s.startedAt).getTime();
          return (
            <li key={s.id} className={`flex items-center gap-1 border-b border-white/10 ${s.id === aktifId ? 'bg-white/15' : ''}`}>
              <button
                type="button"
                onClick={() => onPilih(a)}
                className="min-w-0 flex-1 px-3 py-2 text-left hover:bg-white/10"
              >
                <span className="block text-sm tabular-nums">{formatWibJam(a)}</span>
                <span className="block text-xs text-white/60">
                  {formatWibTanggal(a)} · {formatDuration(s.durationS)} · {formatBytes(s.sizeBytes)}
                </span>
              </button>
              {/* Unduh satu klip apa adanya — tanpa merakit ulang apa pun, jadi
                  tidak lewat antrean export (§D8.2). */}
              <a
                href={`/api/v1/recordings/${s.id}/download`}
                download
                title={t('Download this clip')}
                aria-label={t('Download this clip')}
                className="mr-2 flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3v12" />
                  <path d="m7 10 5 5 5-5" />
                  <path d="M4 21h16" />
                </svg>
              </a>
            </li>
          );
        })}
      </ul>

      {segments.length > MAKS && (
        <p className="shrink-0 border-t border-white/15 px-3 py-2 text-xs text-white/60">
          {t('Showing the first {n} clips — narrow the range to see the rest.', { n: MAKS })}
        </p>
      )}
    </div>
  );
}

/** Zoom jendela timeline di sekitar satu titik, dijaga di dalam rentang filter. */
function zoom(
  v: { from: number; to: number },
  faktor: number,
  pusat: number,
  batasBawah: number,
  batasAtas: number,
): { from: number; to: number } {
  const rentang = v.to - v.from;
  const baru = Math.min(batasAtas - batasBawah, Math.max(2_000, rentang * faktor));
  const rasio = (pusat - v.from) / rentang;
  let from = pusat - rasio * baru;
  let to = from + baru;
  if (from < batasBawah) [from, to] = [batasBawah, batasBawah + baru];
  if (to > batasAtas) [from, to] = [batasAtas - baru, batasAtas];
  return { from: Math.max(batasBawah, from), to: Math.min(batasAtas, to) };
}

/**
 * Satu tombol transport (mundur / maju).
 *
 * Kecepatannya ditulis sebagai angka kecil di pojok, bukan disembunyikan di
 * warna saja: "sedang 4×" adalah keadaan yang harus terbaca sekali lihat, dan
 * §G3 melarang keadaan yang cuma ditandai warna. Angkanya baru muncul di atas
 * 1× — chip "1×" yang selalu menempel hanya jadi kebisingan.
 */
function TombolTransport({
  onClick,
  title,
  label,
  aktif,
  tanda,
  children,
}: {
  onClick: () => void;
  title: string;
  label: string;
  aktif: boolean;
  tanda: string | null;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      aria-pressed={aktif}
      className={`relative flex h-12 w-12 items-center justify-center rounded-full transition-[background-color,scale] duration-150 active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
        aktif ? 'bg-subtle text-accent-text' : 'hover:bg-subtle'
      }`}
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
        {children}
      </svg>
      {tanda && (
        <span className="absolute -bottom-0.5 right-0 rounded-full bg-accent px-1 text-[10px] font-semibold leading-4 text-accent-contrast">
          {tanda}
        </span>
      )}
    </button>
  );
}

/** §G10.1 — peta tombol wajib bisa dilihat dari dalam halaman, bukan cuma di spec. */
function PetaTombol() {
  const { t } = useBahasa();
  const kotakRef = useRef<HTMLDivElement>(null);

  /**
   * Begitu terbuka, panelnya **dibawa ke layar** (diminta lapangan, 7 September
   * 2026: *"di mobile, bagian recordings pas pencet tombol Help ketiknya
   * langsung ke bawah biar gak bikin bingung"*).
   *
   * Halaman Playback tingginya dipatok satu layar (§G10.4) dan yang menggulung
   * adalah `<main>`. Di ponsel, bilah aksinya sendiri sudah membungkus beberapa
   * baris, jadi panel yang lahir **di bawahnya** lahir di luar lipatan: tombol
   * ditekan, `aria-expanded` berubah, dan yang terlihat di layar tidak berubah
   * sama sekali. Yang terjadi berikutnya selalu sama — tombolnya ditekan lagi,
   * dan penekanan kedua itu justru menutup panel yang sebenarnya sudah terbuka.
   *
   * `block: 'nearest'` menggulung **sependek mungkin**: di desktop, tempat
   * panelnya memang sudah terlihat, ia tidak menggeser apa pun. Yang bergerak
   * cuma layar yang memang perlu bergerak.
   *
   * Fokus ikut pindah supaya pembaca layar mengumumkan isinya, tapi dengan
   * `preventScroll` — tanpa itu browser menggulung sendiri secara mendadak dan
   * berebut dengan gulungan halus di bawah ini.
   */
  useEffect(() => {
    const el = kotakRef.current;
    if (!el) return;
    const diam = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ behavior: diam ? 'auto' : 'smooth', block: 'nearest' });
  }, []);

  const baris: [string, string][] = [
    ['Space', 'Play / pause'],
    ['L', 'Forward — press again for 2×, 4×, 8×'],
    ['J', 'Rewind — press again for 2×, 4×, 8×'],
    ['K', 'Stop, back to 1×'],
    ['→ / ←', 'Step one frame'],
    ['Shift + → / ←', 'Step one second'],
    ['+ / −', 'Zoom the timeline'],
    ['Home / End', 'To the visible edge — press again to page one screen'],
    ['PgUp / PgDn', 'Previous / next clip'],
    ['Alt + I / O', 'Clear mark in / mark out'],
    ['I / O', 'Mark in / mark out'],
    ['Shift + I / O', 'Jump to mark in / out'],
    ['M', 'Add a bookmark here'],
    ['N / Shift + N', 'Next / previous bookmark'],
    ['F', 'Fullscreen'],
    ['Esc', 'Clear mark in / out'],
  ];
  return (
    <div
      ref={kotakRef}
      // `tabIndex={-1}`: bisa difokus program, tidak pernah ikut urutan Tab.
      // Panel ini tidak punya satu pun kendali di dalamnya, jadi menambahkannya
      // ke urutan Tab hanya menambah satu perhentian yang tidak bisa diapa-apakan.
      tabIndex={-1}
      role="group"
      aria-labelledby="peta-tombol-judul"
      className="mt-2 shrink-0 scroll-mt-2 rounded-lg border border-line bg-surface p-3 text-sm focus:outline-none"
    >
      {/* Tombol pembukanya bernama "Help" sejak 7 September 2026, jadi judulnya
          pindah ke sini: isinya tetap peta tombol, dan orang yang membukanya
          harus langsung tahu itu — bukan mengira bantuan yang dicarinya tidak
          ada di sini. */}
      <h2
        id="peta-tombol-judul"
        className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted"
      >
        {t('Keyboard shortcuts')}
      </h2>
      <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {baris.map(([k, v]) => (
          <div key={k} className="flex items-baseline gap-2">
            <kbd className="rounded border border-line bg-subtle px-1.5 text-xs">{k}</kbd>
            <span className="text-muted">{t(v)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
